import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { OPEN_CONVERSATION_KEY } from '../components/ChatwootAssignmentToastListener';
import CenteredMessage from '../components/CenteredMessage';
import { API_BASE_URL } from '../config/env';
import {
  enrichAtencionFarmaciaCardsWithNames,
  fetchAtencionPedido,
  formatAtencionPedidoNro,
  isAtencionFarmaciaPedido,
  loadAtencionPedidosForFarmacia,
  loadAtencionSucursalesOperativas,
  normalizeAtencionFarmaciaCard,
  resolveAtencionPedidoId,
  updateAtencionPedidoFarmaciaEstado
} from '../api/atencionPedidos';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import '../styles/gestion-derivacion-premium.css';
import '../styles/farmacia-premium.css';
import '../styles/dispensa-premium.css';
// lazy import map to avoid breaking environments without deps installed
const DeliveryMap = React.lazy(() => import('../components/DeliveryMap'));

const DISPENSA_KANBAN_COLUMNS = [
  { key: 'pendiente_call_center', title: 'Sin gestión', subtitle: 'Bot recetas crónicas' },
  { key: 'pedido_generado_farmacia', title: 'En farmacia', subtitle: 'Pedido generado' },
  { key: 'en_preparacion', title: 'En preparación', subtitle: '' },
  { key: 'en_camino', title: 'Entregado al cadete', subtitle: '' },
  { key: 'finalizados', title: 'Finalizados', subtitle: '' }
];

const KANBAN_BOARD_PAGE_SIZE = 3;

const FARMACIA_ESTADO_FLOW = ['pedido_generado_farmacia', 'en_preparacion', 'en_camino'];

function isDispensaReadonlyColumn(colKey) {
  return colKey === 'finalizados';
}

function isDispensaCardDraggable(estado) {
  const e = String(estado || '');
  return e === 'pendiente_call_center' || e === 'pedido_generado_farmacia' || e === 'en_preparacion';
}

function getFarmaciaAllowedTransitions(estadoActual) {
  const e = String(estadoActual || '');
  if (e === 'pedido_generado_farmacia') return ['en_preparacion'];
  if (e === 'en_preparacion') return ['pedido_generado_farmacia', 'en_camino'];
  return [];
}

function getFarmaciaSiguienteEstado(estadoActual) {
  if (estadoActual === 'pedido_generado_farmacia') return 'en_preparacion';
  if (estadoActual === 'en_preparacion') return 'en_camino';
  return null;
}

function normalizeFarmaciaFocusKey(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (s.startsWith('atencion-')) return s.toLowerCase();
  if (/^at-\d+$/i.test(s)) return `atencion-${s.slice(3)}`;
  if (/^\d+$/.test(s)) return `atencion-${s}`;
  return s.toLowerCase();
}

function pedidoMatchesFarmaciaFocus(pedido, focusKey) {
  const key = normalizeFarmaciaFocusKey(focusKey);
  if (!key || !pedido) return false;
  const cardId = String(pedido.id || '').trim().toLowerCase();
  const apid = pedido.atencion_pedido_id ? `atencion-${Number(pedido.atencion_pedido_id)}` : '';
  const nro = String(pedido.nro_pedido || '').trim().toUpperCase();
  const numeric = key.startsWith('atencion-') ? key.slice('atencion-'.length) : key;
  if (cardId && cardId === key) return true;
  if (apid && apid === key) return true;
  if (nro && (nro === key.toUpperCase() || nro === `AT-${numeric}`)) return true;
  if (cardId === `atencion-${numeric}`) return true;
  return false;
}

function getPedidoMetodoEntregaKey(pedido) {
  const key = String(pedido?.metodo_entrega || '').trim().toLowerCase();
  return key === 'mostrador' || key === 'domicilio' ? key : '';
}

function getPedidoSucursalCodigo(pedido) {
  return String(pedido?.sucursal_codigo || pedido?.sucursal?.codigo || '').trim();
}

function getPedidoSucursalNombre(pedido) {
  return String(pedido?.sucursal_nombre || pedido?.sucursal?.nombre || '').trim();
}

function formatPedidoMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function getPedidoItemCantidad(item) {
  const qty = Number(item?.cantidad);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

function getPedidoOptionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getPedidoItemTotal(item) {
  const explicitTotal = getPedidoOptionalNumber(item?.total_linea);
  if (explicitTotal !== null) return explicitTotal;
  const unit = getPedidoOptionalNumber(item?.precio_unitario);
  if (unit === null) return null;
  return unit * getPedidoItemCantidad(item);
}

function getPedidoItemsTotal(pedido) {
  const explicitTotal = getPedidoOptionalNumber(pedido?.total_pedido);
  if (explicitTotal !== null) return explicitTotal;
  const items = Array.isArray(pedido?.items) ? pedido.items : [];
  const totals = items
    .map((item) => getPedidoItemTotal(item))
    .filter((value) => Number.isFinite(value));
  if (!totals.length) return null;
  return totals.reduce((acc, value) => acc + value, 0);
}

function isBotMostradorPedido(pedido) {
  if (!pedido) return false;
  const metodoEntrega = getPedidoMetodoEntregaKey(pedido);
  return metodoEntrega === 'mostrador';
}

function isBotFarmaciaPedido(pedido) {
  if (!pedido) return false;
  if (getPedidoMetodoEntregaKey(pedido)) return true;
  const origen = String(pedido?.origen || '').trim().toLowerCase();
  if (origen === 'bot_mostrador' || origen === 'bot_domicilio' || origen === 'bot_atencion' || origen === 'atencion_bot') {
    return true;
  }
  return isAtencionFarmaciaPedido(pedido);
}

function isBotFarmaciaDomicilioPedido(pedido) {
  if (!isBotFarmaciaPedido(pedido)) return false;
  const metodoEntrega = getPedidoMetodoEntregaKey(pedido);
  return metodoEntrega === 'domicilio';
}

function isBotTratamientosCronicosPedido(pedido) {
  if (!pedido || isBotFarmaciaPedido(pedido)) return false;
  const ingreso = String(pedido?.ingreso_origen || '').trim().toLowerCase();
  if (ingreso === 'call_center_crm') return false;
  const origen = String(pedido?.origen || 'recetas_emitidas').trim().toLowerCase();
  return origen === 'recetas_emitidas';
}

function canRevertirPedidoASinGestion(pedido) {
  return isBotTratamientosCronicosPedido(pedido) && !isAtencionFarmaciaPedido(pedido);
}

function getFarmaciaOrigenBadge(pedido) {
  if (isBotFarmaciaPedido(pedido)) return { label: 'Bot Farmacia', variant: 'mostrador' };
  if (isBotTratamientosCronicosPedido(pedido)) return { label: 'Bot recetas crónicas', variant: 'cronicas' };
  return null;
}

function getPedidoNotasRaw(pedido) {
  return String(pedido?.notas || pedido?.observaciones || '').trim();
}

function hasPedidoNotasFarmacia(pedido) {
  if (isAtencionFarmaciaPedido(pedido)) return false;
  const value = getPedidoNotasRaw(pedido);
  return !!value && value.toLowerCase() !== 'sin notas';
}

function origenHistLabel(origen) {
  const key = String(origen || '').trim().toLowerCase();
  if (key === 'farmacia_kanban') return 'Kanban Farmacia';
  if (key === 'farmacia_modal') return 'Panel Farmacia';
  if (key === 'call_center') return 'Call Center / Dispensa';
  if (key === 'cadeteria') return 'Delivery';
  if (key === 'sistema') return 'Sistema';
  if (key === 'bot_mostrador') return 'Bot Farmacia';
  if (key === 'bot_atencion') return 'Bot Farmacia';
  if (key === 'atencion_bot') return 'Bot Farmacia';
  return origen ? String(origen).replace(/_/g, ' ') : '—';
}

const PENDIENTE_SYSTEM_COPY_RE = [
  /preparaci[oó]n\s+solicitada\s+por\s+whatsapp\s+tras\s+receta\s+emitida/i,
  /preparaci[oó]n\s+solicitada\s+por\s+whatsapp/i,
  /solicitada\s+por\s+whatsapp\s+tras\s+receta\s+emitida/i
];

function isPendienteSystemCopy(text) {
  const s = String(text || '').trim();
  if (!s) return true;
  return PENDIENTE_SYSTEM_COPY_RE.some((re) => re.test(s));
}

function stripPendienteSystemCopy(text) {
  const s = String(text || '').trim();
  if (!s || isPendienteSystemCopy(s)) return '';
  return s;
}

function formatPendienteMedLabel(rawName) {
  const name = stripPendienteSystemCopy(rawName);
  if (!name) return '';
  return name
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      if (word.length <= 1) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function getPendienteMedicamentoDisplay(pedido) {
  const candidates = [
    pedido?.nombre_generico,
    pedido?.nombre_comercial,
    pedido?.medicamento,
    pedido?.nombre_generico_med
  ];
  for (const raw of candidates) {
    const label = formatPendienteMedLabel(raw);
    if (label) return label;
  }
  return 'Medicamento a confirmar';
}

function parsePedidoFecha(fechaRaw) {
  if (!fechaRaw) return null;
  try {
    const raw = String(fechaRaw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    let s = raw.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
    const d2 = new Date(fechaRaw);
    return Number.isNaN(d2.getTime()) ? null : d2;
  } catch (_) {
    return null;
  }
}

function formatHistFecha(fechaRaw) {
  if (!fechaRaw) return '—';
  const d = parsePedidoFecha(fechaRaw);
  if (!d) return String(fechaRaw);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${min} hs`;
}

function getFinalizadoCardVariant(estado) {
  return String(estado || '').toLowerCase() === 'cancelado' ? 'cancelado' : 'entregado';
}

function getFinalizadoStampLabel(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'cancelado') return 'Anul.';
  if (e === 'entregado') return 'Listo';
  return 'Ok';
}

function getDispensaReadonlyMeta(colKey) {
  return {
    icon: '✓',
    title: 'Pedidos cerrados por Delivery',
    hint: 'Entrega o cancelación confirmada. Acá solo consultás el resultado.',
    empty: 'Sin pedidos finalizados',
    emptyHint: 'Aparecen cuando Delivery confirma la entrega',
    ariaTitle: 'Solo consulta. Delivery registra entregas y cancelaciones.'
  };
}

export default function PedidosCallCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPedidoKey = normalizeFarmaciaFocusKey(searchParams.get('focus'));
  const focusCardRef = useRef(null);
  const atencionEnrichAttemptedRef = useRef(new Set());
  const { user, permissionsLoading } = useAuth();
  const [data, setData] = useState({ pedidos_farmacia: [] });
  const [initialLoading, setInitialLoading] = useState(true);
  // UI state hooks (must be declared unconditionally)
  const [modalFarmaciaOpen, setModalFarmaciaOpen] = useState(false);
  const [modalEstadoOpen, setModalEstadoOpen] = useState(false);
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [mapDrawerAddress, setMapDrawerAddress] = useState('');
  const [mapDrawerLatitud, setMapDrawerLatitud] = useState(null);
  const [mapDrawerLongitud, setMapDrawerLongitud] = useState(null);
  const [mapDrawerPaciente, setMapDrawerPaciente] = useState('');
  const [mapDrawerMedicamento, setMapDrawerMedicamento] = useState('');
  const [mapDrawerLaboratorio, setMapDrawerLaboratorio] = useState('');
  const [mapDrawerNroPedido, setMapDrawerNroPedido] = useState('');
  const [pedidoDetailOpen, setPedidoDetailOpen] = useState(false);
  const [pedidoDetailLoading, setPedidoDetailLoading] = useState(false);
  const [pedidoDetailError, setPedidoDetailError] = useState('');
  const [pedidoDetailData, setPedidoDetailData] = useState(null);
  const [selectedFarmId, setSelectedFarmId] = useState(null);
  const [farmNro, setFarmNro] = useState('');
  const [farmNotas, setFarmNotas] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState('pedido_generado_farmacia');
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1366,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  }));

  useEffect(() => {
    const onResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    return undefined;
  }, []);

  const isMobile = viewport.width <= 768;
  const isTablet = viewport.width > 768 && viewport.width <= 1100;
  const isSmallLandscape = viewport.width <= 1024 && viewport.width > viewport.height;
  // Table controls: search, pagination and page size
  const parseFechaMs = (fechaRaw) => {
    if (!fechaRaw) return 0;
    try {
      let s = String(fechaRaw);
      s = s.replace(' ', 'T');
      s = s.replace(/(\.\d{3})\d+/, '$1');
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return d.getTime();
      const d2 = new Date(fechaRaw);
      return Number.isNaN(d2.getTime()) ? 0 : d2.getTime();
    } catch (e) { return 0; }
  };
  const getTodayLocalISO = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const isPedidoPendienteAnterior = (pedido) => {
    if (!pedido) return false;
    if (isAtencionFarmaciaPedido(pedido)) return false;
    const estado = String(pedido.estado || '').toLowerCase();
    // "en_camino" en Farmacia = "Entregado al cadete": ya salió del flujo, no es backlog
    if (estado === 'entregado' || estado === 'cancelado' || estado === 'en_camino') return false;
    const ts = parseFechaMs(pedido.fecha);
    if (!ts) return false;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return ts < todayStart.getTime();
  };

  const isPedidoEstadoTerminal = (estado) => {
    const e = String(estado || '').toLowerCase();
    return e === 'entregado' || e === 'cancelado';
  };

  const sortPedidos = (arr) => {
    if (!Array.isArray(arr)) return [];
    const priority = {
      'pedido_generado_farmacia': 0,
      'en_preparacion': 1,
      'en_camino': 2,
      'entregado': 3,
      'cancelado': 4,
      'pendiente_call_center': 5
    };
    return arr.slice().sort((a, b) => {
      const pa = priority[a.estado] !== undefined ? priority[a.estado] : 10;
      const pb = priority[b.estado] !== undefined ? priority[b.estado] : 10;
      if (pa !== pb) return pa - pb; // lower priority value first
      const ta = parseFechaMs(a.fecha);
      const tb = parseFechaMs(b.fecha);
      return tb - ta; // newer first
    });
  };

  const pedidosFarm = useMemo(() => sortPedidos(data.pedidos_farmacia || []), [data.pedidos_farmacia]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [filtroRangoModo, setFiltroRangoModo] = useState('solo_hoy');
  const [filtroBotOrigen, setFiltroBotOrigen] = useState('todos');
  const [filtroBotEntrega, setFiltroBotEntrega] = useState('todos');
  const [filtroBotSucursal, setFiltroBotSucursal] = useState('todos');
  const [sucursalesOperativas, setSucursalesOperativas] = useState([]);
  const [kanbanPage, setKanbanPage] = useState(1);
  const [historialGlobalOpen, setHistorialGlobalOpen] = useState(false);
  const [historialGlobalDni, setHistorialGlobalDni] = useState('');
  const [historialGlobalPacienteSearch, setHistorialGlobalPacienteSearch] = useState('');
  const [historialGlobalSearch, setHistorialGlobalSearch] = useState('');
  const [historialGlobalDesde, setHistorialGlobalDesde] = useState('');
  const [historialGlobalHasta, setHistorialGlobalHasta] = useState('');
  const [historialGlobalOrder, setHistorialGlobalOrder] = useState('fecha_desc');
  const [historialGlobalPage, setHistorialGlobalPage] = useState(1);
  const [historialGlobalItemsPerPage, setHistorialGlobalItemsPerPage] = useState(6);
  const [draggingPedidoId, setDraggingPedidoId] = useState(null);
  const [dragOverEstado, setDragOverEstado] = useState('');
  const [pendienteInfoDrawerOpen, setPendienteInfoDrawerOpen] = useState(false);
  const [pendienteInfoPedido, setPendienteInfoPedido] = useState(null);
  const [pendienteEditDrawerOpen, setPendienteEditDrawerOpen] = useState(false);
  const [pendienteEditPedido, setPendienteEditPedido] = useState(null);
  const [pendienteEditDni, setPendienteEditDni] = useState('');
  const [notasPopupPedido, setNotasPopupPedido] = useState(null);
  const [trazabilidadPedido, setTrazabilidadPedido] = useState(null);
  const [trazabilidadItems, setTrazabilidadItems] = useState([]);
  const [trazabilidadLoading, setTrazabilidadLoading] = useState(false);
  const [pendienteEditDireccion, setPendienteEditDireccion] = useState('');
  const [pendienteEditNotas, setPendienteEditNotas] = useState('');
  const [pendienteEditSaving, setPendienteEditSaving] = useState(false);
  const pedidoDetailItems = Array.isArray(pedidoDetailData?.items) ? pedidoDetailData.items : [];
  const pedidoDetailTotal = getPedidoItemsTotal(pedidoDetailData);

  /** Alineado con Cadetería: en backlog sin fechas, finalizados siempre visibles en tablero. */
  const matchesDispensaBoardDateFilter = (pedido) => {
    if (!pedido) return false;
    if (isPedidoEstadoTerminal(pedido.estado)) {
      if (filtroRangoModo === 'hoy_anteriores' && !filtroDesde && !filtroHasta) return true;
    } else if (filtroRangoModo === 'solo_hoy' && isPedidoPendienteAnterior(pedido)) {
      return false;
    } else if (filtroRangoModo === 'hoy_anteriores' && isPedidoPendienteAnterior(pedido)) {
      return true;
    }
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const todayIso = getTodayLocalISO();
    const desdeMs = toMs(filtroDesde || todayIso);
    const hastaMs = toMs(filtroHasta || todayIso);
    const ts = parseFechaMs(pedido.fecha);
    if (ts == null) return true;
    if (desdeMs && ts < desdeMs) return false;
    if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
    return true;
  };

  const pedidosHistorial = useMemo(
    () => pedidosFarm.filter((p) => isPedidoEstadoTerminal(p.estado)),
    [pedidosFarm]
  );
  const pedidosVisibles = useMemo(
    () => pedidosFarm.filter((p) => !isPedidoEstadoTerminal(p.estado)),
    [pedidosFarm]
  );

  useEffect(() => {
    let cancelled = false;
    loadAtencionSucursalesOperativas()
      .then((rows) => {
        if (!cancelled) setSucursalesOperativas(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSucursalesOperativas([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sucursalFilterOptions = useMemo(
    () => (sucursalesOperativas || []).map((s) => ({
      codigo: String(s?.codigo || '').trim(),
      nombre: String(s?.nombre || '').trim(),
      localidad: String(s?.localidad || '').trim()
    })).filter((s) => s.codigo && s.nombre),
    [sucursalesOperativas]
  );

  const matchesBotOriginFilter = (pedido) => {
    if (filtroBotOrigen === 'cronicas') return isBotTratamientosCronicosPedido(pedido);
    if (filtroBotOrigen === 'farmacia') {
      if (!isBotFarmaciaPedido(pedido)) return false;
      if (filtroBotEntrega === 'mostrador' && !isBotMostradorPedido(pedido)) return false;
      if (filtroBotEntrega === 'domicilio' && !isBotFarmaciaDomicilioPedido(pedido)) return false;
      if (filtroBotSucursal === 'sin_asignar') return !getPedidoSucursalCodigo(pedido);
      if (filtroBotSucursal !== 'todos') return getPedidoSucursalCodigo(pedido) === filtroBotSucursal;
      return true;
    }
    return true;
  };
  const pedidosVisiblesFiltradosPorBot = useMemo(
    () => pedidosVisibles.filter((p) => matchesBotOriginFilter(p)),
    [pedidosVisibles, filtroBotOrigen, filtroBotEntrega, filtroBotSucursal]
  );
  const backlogPendienteCount = useMemo(
    () => pedidosVisiblesFiltradosPorBot.filter((p) => isPedidoPendienteAnterior(p)).length,
    [pedidosVisiblesFiltradosPorBot]
  );

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return pedidosVisiblesFiltradosPorBot.filter((p) => {
      const matchText = !q || (
        String(p.paciente || '').toLowerCase().includes(q) ||
        String(p.cliente_dni || '').toLowerCase().includes(q) ||
        String(p.medicamento || '').toLowerCase().includes(q) ||
        String(p.nro_pedido || '').toLowerCase().includes(q) ||
        String(p.direccion || '').toLowerCase().includes(q) ||
        getPedidoSucursalNombre(p).toLowerCase().includes(q)
      );
      if (!matchText) return false;
      return matchesDispensaBoardDateFilter(p);
    });
  }, [pedidosVisiblesFiltradosPorBot, search, filtroDesde, filtroHasta, filtroRangoModo]);

  const historialPacientes = useMemo(() => {
    const map = new Map();
    pedidosHistorial.forEach((p) => {
      const dni = String(getPedidoDni(p) || '').replace(/\D/g, '');
      if (!dni) return;
      const nombre = getPedidoPacienteDisplay(p) || `DNI ${dni}`;
      if (!map.has(dni)) map.set(dni, { dni, nombre });
    });
    return Array.from(map.values()).sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
  }, [pedidosHistorial]);

  const filteredHistorialPacientes = useMemo(() => {
    const q = String(historialGlobalPacienteSearch || '').trim().toLowerCase();
    if (!q) return historialPacientes;
    return historialPacientes.filter((p) => String(p.nombre || '').toLowerCase().includes(q) || String(p.dni || '').includes(q));
  }, [historialPacientes, historialGlobalPacienteSearch]);

  const historialGlobalItems = useMemo(() => {
    if (!historialGlobalDni) return [];
    return pedidosHistorial.filter((p) => String(getPedidoDni(p) || '').replace(/\D/g, '') === String(historialGlobalDni));
  }, [pedidosHistorial, historialGlobalDni]);

  const filteredHistorialGlobalItems = useMemo(() => {
    const q = String(historialGlobalSearch || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const desdeMs = toMs(historialGlobalDesde);
    const hastaMs = toMs(historialGlobalHasta);
    let rows = historialGlobalItems.filter((p) => {
      if (!q) return true;
      return (
        String(getPedidoPacienteDisplay(p) || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
        || getPedidoSucursalNombre(p).toLowerCase().includes(q)
      );
    });
    rows = rows.filter((p) => {
      const ts = parseFechaMs(p.fecha);
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const aTs = parseFechaMs(a.fecha);
      const bTs = parseFechaMs(b.fecha);
      if (historialGlobalOrder === 'fecha_asc') return aTs - bTs;
      return bTs - aTs;
    });
    return rows;
  }, [historialGlobalItems, historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalOrder]);

  const totalHistorialGlobalPages = Math.max(1, Math.ceil(filteredHistorialGlobalItems.length / historialGlobalItemsPerPage));
  useEffect(() => {
    if (historialGlobalPage > totalHistorialGlobalPages) setHistorialGlobalPage(1);
  }, [historialGlobalPage, totalHistorialGlobalPages]);
  useEffect(() => {
    setHistorialGlobalPage(1);
  }, [historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalOrder, historialGlobalDni, historialGlobalItemsPerPage]);
  const pageHistorialGlobalItems = useMemo(() => (
    filteredHistorialGlobalItems.slice((historialGlobalPage - 1) * historialGlobalItemsPerPage, historialGlobalPage * historialGlobalItemsPerPage)
  ), [filteredHistorialGlobalItems, historialGlobalPage, historialGlobalItemsPerPage]);
  const boardItems = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return pedidosFarm.filter((p) => {
      if (!matchesBotOriginFilter(p)) return false;
      const matchText = !q || (
        String(p.paciente || '').toLowerCase().includes(q)
        || String(p.cliente_dni || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
      );
      if (!matchText) return false;
      return matchesDispensaBoardDateFilter(p);
    });
  }, [pedidosFarm, search, filtroDesde, filtroHasta, filtroRangoModo, filtroBotOrigen, filtroBotEntrega, filtroBotSucursal]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  useEffect(() => { if (page > totalPages) setPage(1); }, [filtered.length, itemsPerPage]);
  const pageItems = useMemo(() => filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage), [filtered, page, itemsPerPage]);

  const normalizePedidosRows = (rows) => (Array.isArray(rows) ? rows : []).map((x) => ({
    ...x,
    origen: x.origen || null,
    ingreso_origen: x.ingreso_origen || null,
    metodo_entrega: x.metodo_entrega || null,
    direccion_envio: x.direccion_envio || x.direccion_entrega || null,
    paciente: x.paciente || x.paciente_nombre || x.solicitante || 'Paciente',
    medicamento: x.medicamento || x.medicamento_nombre || x.nombre_generico || 'Sin medicamento',
    presentacion: x.presentacion || x.concentracion || '',
    direccion: x.direccion || x.direccion_entrega || '',
    nro_pedido: x.nro_pedido || x.nro_pedido_farmacia || '',
    estado: x.estado || 'pendiente_call_center',
    fecha: x.fecha || (x.fecha_solicitud ? x.fecha_solicitud.split('T')[0] : new Date().toISOString().split('T')[0])
  }));

  const normalizeAtencionRows = (atencionRowsRaw) => (Array.isArray(atencionRowsRaw) ? atencionRowsRaw : []).map((x) => {
    const metodoEntrega = String(x.metodo_entrega || '').trim().toLowerCase();
    const pedidoId =
      x.atencion_pedido_id ||
      (String(x.id || '').startsWith('atencion-') ? Number(String(x.id).slice('atencion-'.length)) : null) ||
      (Number.isFinite(Number(x.id)) && !String(x.id).includes('-') ? Number(x.id) : null);
    const cardId = String(x.id || '').startsWith('atencion-')
      ? x.id
      : (pedidoId ? `atencion-${pedidoId}` : x.id);
    return {
      ...x,
      id: cardId,
      atencion_pedido_id: pedidoId,
      origen: x.origen || (metodoEntrega === 'domicilio' ? 'bot_domicilio' : 'bot_mostrador'),
      metodo_entrega: metodoEntrega || null,
      direccion_envio: x.direccion_envio || null,
      phone: x.phone || null,
      telefono: x.telefono || '',
      cliente_nombre: x.cliente_nombre || null,
      paciente: x.paciente || x.cliente_nombre || 'Cliente mostrador',
      medicamento: x.medicamento || 'Sin medicamento',
      presentacion: x.presentacion || '',
      direccion: x.direccion || 'Retiro en mostrador',
      nro_pedido: formatAtencionPedidoNro(x.nro_pedido, pedidoId),
      estado: x.estado || x.farmacia_estado || 'pedido_generado_farmacia',
      fecha: x.fecha || new Date().toISOString().split('T')[0]
    };
  });

  const mergePedidosWithAtencion = (classicRows, atencionRowsRaw) => {
    const normalized = normalizePedidosRows(classicRows);
    const atencionRows = normalizeAtencionRows(atencionRowsRaw);
    const seen = new Set(normalized.map((p) => String(p.id)));
    const mergedAtencion = atencionRows.filter((p) => !seen.has(String(p.id)));
    return [...normalized, ...mergedAtencion];
  };

  useEffect(() => {
    if (filtroBotOrigen !== 'farmacia' && filtroBotEntrega !== 'todos') {
      setFiltroBotEntrega('todos');
    }
    if (filtroBotOrigen !== 'farmacia' && filtroBotSucursal !== 'todos') {
      setFiltroBotSucursal('todos');
    }
  }, [filtroBotEntrega, filtroBotOrigen, filtroBotSucursal]);

  useEffect(() => {
    setPage(1);
  }, [filtroBotOrigen, filtroBotEntrega, filtroBotSucursal]);

  useEffect(() => {
    setKanbanPage(1);
  }, [search, filtroDesde, filtroHasta, filtroRangoModo, filtroBotOrigen, filtroBotEntrega, filtroBotSucursal]);

  // Load pedidos de dispensa + bot farmacia + refresh periódico
  useEffect(() => {
    let mounted = true;
    const metodoEntregaFiltro =
      filtroBotOrigen === 'farmacia' && (filtroBotEntrega === 'mostrador' || filtroBotEntrega === 'domicilio')
        ? filtroBotEntrega
        : undefined;
    const sucursalCodigoFiltro =
      filtroBotOrigen === 'farmacia' && filtroBotSucursal !== 'todos' && filtroBotSucursal !== 'sin_asignar'
        ? filtroBotSucursal
        : undefined;

    const loadPedidosCallCenter = async () => {
      try {
        let fetched = [];
        const tryEndpoints = [
          `${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`,
          `${API_BASE_URL}/api/asociacion-recetas/pedidos-callcenter`
        ];
        for (const ep of tryEndpoints) {
          try {
            const r = await fetch(ep, { credentials: 'include' });
            if (!r.ok) continue;
            const j = await r.json().catch(() => []);
            const rows = Array.isArray(j) ? j : (Array.isArray(j?.pedidos) ? j.pedidos : []);
            if (rows.length) {
              fetched = rows;
              break;
            }
          } catch (_) {
            // try next endpoint
          }
        }

        if (!fetched.length) {
          try {
            const r2 = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, { credentials: 'include' });
            if (r2.ok) {
              const j2 = await r2.json();
              const raw = Array.isArray(j2) ? j2 : (Array.isArray(j2?.pedidos) ? j2.pedidos : []);
              fetched = raw.filter((x) => ['pedido_generado_farmacia', 'pendiente_call_center', 'en_preparacion', 'en_camino', 'entregado', 'cancelado'].includes(x.estado));
            }
          } catch (_) {
            // silent
          }
        }

        let atencionRows = [];
        try {
          atencionRows = await loadAtencionPedidosForFarmacia({
            limit: 150,
            metodoEntrega: metodoEntregaFiltro,
            sucursalCodigo: sucursalCodigoFiltro
          });
        } catch (_) {
          // silent
        }

        if (mounted) {
          setData({ pedidos_farmacia: mergePedidosWithAtencion(fetched, atencionRows) });
        }
      } catch (e) {
        console.error('Error cargando pedidos de dispensa:', e);
      } finally {
        if (mounted) setInitialLoading(false);
      }
    };

    loadPedidosCallCenter();
    const timer = setInterval(() => {
      if (!document.hidden) loadPedidosCallCenter();
    }, 15000);
    const onFocus = () => {
      if (!document.hidden) loadPedidosCallCenter();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [filtroBotEntrega, filtroBotOrigen, filtroBotSucursal]);

  useEffect(() => {
    let cancelled = false;
    const list = Array.isArray(data.pedidos_farmacia) ? data.pedidos_farmacia : [];
    const pending = list.filter((p) => {
      if (!isAtencionFarmaciaPedido(p)) return false;
      const id = String(p.id);
      if (atencionEnrichAttemptedRef.current.has(id)) return false;
      const normalized = normalizeAtencionFarmaciaCard(p);
      return Boolean(normalized.telefono) && !normalized.cliente_nombre;
    });
    if (!pending.length) return undefined;

    pending.forEach((p) => atencionEnrichAttemptedRef.current.add(String(p.id)));
    enrichAtencionFarmaciaCardsWithNames(pending).then((enriched) => {
      if (cancelled) return;
      const byId = new Map(enriched.map((card) => [String(card.id), card]));
      setData((prev) => ({
        ...prev,
        pedidos_farmacia: (prev.pedidos_farmacia || []).map((p) => byId.get(String(p.id)) || p)
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [data.pedidos_farmacia]);

  const focusedPedido = useMemo(() => {
    if (!focusPedidoKey) return null;
    return (data.pedidos_farmacia || []).find((p) => pedidoMatchesFarmaciaFocus(p, focusPedidoKey)) || null;
  }, [focusPedidoKey, data.pedidos_farmacia]);

  useEffect(() => {
    if (!focusPedidoKey || initialLoading) return undefined;
    const t = setTimeout(() => {
      focusCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(t);
  }, [focusPedidoKey, initialLoading, focusedPedido?.id]);

  const clearFarmaciaFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  // Accordion: open first item on initial render of the page list
  const [openId, setOpenId] = useState(null);
  const _initOpenRef = useRef(false);
  const contentRefs = useRef({});
  useEffect(() => {
    if (_initOpenRef.current) return;
    if (pageItems && pageItems.length > 0) {
      setOpenId(pageItems[0].id);
      _initOpenRef.current = true;
    }
  }, [pageItems]);
  const [pageInput, setPageInput] = useState(String(page));
  useEffect(() => {
    setPageInput(String(page || 1));
  }, [page, totalPages]);

  const handlePageInputChange = (e) => {
    const raw = e.target.value;
    const numeric = raw.replace(/\D/g, '');
    setPageInput(numeric);
  };

  const goToInputPage = () => {
    if (!pageInput) return;
    let next = Number(pageInput);
    if (Number.isNaN(next)) return;
    if (next < 1) next = 1;
    if (next > totalPages) next = totalPages;
    if (next === page) return;
    setPage(next);
  };
  const kpiSinGestion = useMemo(
    () => boardItems.filter((p) => String(p.estado) === 'pendiente_call_center').length,
    [boardItems]
  );
  const kpiEnFarmacia = useMemo(
    () => boardItems.filter((p) => String(p.estado) === 'pedido_generado_farmacia').length,
    [boardItems]
  );
  const kpiEnPreparacion = useMemo(
    () => boardItems.filter((p) => String(p.estado) === 'en_preparacion').length,
    [boardItems]
  );
  const kpiEntregadoCadete = useMemo(
    () => boardItems.filter((p) => String(p.estado) === 'en_camino').length,
    [boardItems]
  );
  const kpiFinalizados = useMemo(
    () => boardItems.filter((p) => isPedidoEstadoTerminal(p.estado)).length,
    [boardItems]
  );

  const kanbanColumnItems = useMemo(() => ({
    pendiente_call_center: boardItems.filter((p) => String(p.estado) === 'pendiente_call_center'),
    pedido_generado_farmacia: boardItems.filter((p) => String(p.estado) === 'pedido_generado_farmacia'),
    en_preparacion: boardItems.filter((p) => String(p.estado) === 'en_preparacion'),
    en_camino: boardItems.filter((p) => String(p.estado) === 'en_camino'),
    finalizados: boardItems.filter((p) => isPedidoEstadoTerminal(p.estado))
  }), [boardItems]);

  const totalKanbanPages = Math.max(
    1,
    ...Object.values(kanbanColumnItems).map((items) => Math.max(1, Math.ceil(items.length / KANBAN_BOARD_PAGE_SIZE)))
  );
  useEffect(() => {
    if (kanbanPage > totalKanbanPages) setKanbanPage(totalKanbanPages);
  }, [kanbanPage, totalKanbanPages]);
  const kanbanPageCards = useMemo(
    () => Object.values(kanbanColumnItems).reduce((acc, items) => {
      const pageSlice = items.slice((kanbanPage - 1) * KANBAN_BOARD_PAGE_SIZE, kanbanPage * KANBAN_BOARD_PAGE_SIZE);
      return acc + pageSlice.length;
    }, 0),
    [kanbanColumnItems, kanbanPage]
  );
  const pendienteDrawerFecha = formatFechaSoloDia(pendienteInfoPedido?.fecha);
  const pendienteDrawerPaciente = getPedidoPacienteDisplay(pendienteInfoPedido);
  const pendienteDrawerDni = formatDni(getPedidoDni(pendienteInfoPedido)) || '—';
  const pendienteDrawerEstado = estadoLabel(pendienteInfoPedido?.estado);
  const pendienteDrawerMedicamento = formatMedicamentoLabel(pendienteInfoPedido?.medicamento || pendienteInfoPedido?.nombre_generico || pendienteInfoPedido?.nombre_generico_med || '—');
  const pendienteDrawerPresentacion = String(pendienteInfoPedido?.presentacion || pendienteInfoPedido?.concentracion || '—');
  const pendienteDrawerDireccion = String(pendienteInfoPedido?.direccion || 'Sin dirección');
  const pendienteDrawerNumeroPedido = String(pendienteInfoPedido?.nro_pedido || 'Sin N°');
  const pendienteDrawerNotas = String(pendienteInfoPedido?.notas || pendienteInfoPedido?.observaciones || 'Sin notas');

  if (permissionsLoading) {
    return (
      <section className="crm-gd-page crm-farm-page crm-pbody">
        <div className="crm-gd-loading" role="status" aria-live="polite">
          <div className="crm-gd-spinner" aria-hidden="true" />
          <div className="crm-gd-loading__title">Cargando permisos</div>
        </div>
      </section>
    );
  }
  const hasPermission = user && Array.isArray(user.permissions) && user.permissions.includes('ver_pedidos_dispensa');
  if (!hasPermission) return <CenteredMessage title="Acceso denegado" message="No tenés permiso para ver Pedidos de dispensa" icon="🔒" />;

  function estadoLabel(estado) {
    if (estado === 'pendiente_call_center') return 'Sin gestión';
    if (estado === 'pedido_generado_farmacia') return 'En farmacia';
    if (estado === 'en_preparacion') return 'En preparación';
    if (estado === 'en_camino') return 'En camino';
    if (estado === 'entregado') return 'Entregado';
    if (estado === 'cancelado') return 'Cancelado';
    return estado;
  }

  function estadoTone(estado) {
    if (estado === 'pendiente_call_center') return { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' };
    if (estado === 'pedido_generado_farmacia') return { bg: '#FEF8EE', text: '#9A6700', border: '#F6DEB2' };
    if (estado === 'en_preparacion') return { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' };
    if (estado === 'en_camino') return { bg: '#EEF4FB', text: '#1C4F86', border: '#D4E1F1' };
    if (estado === 'entregado') return { bg: '#E5F8F6', text: '#00796B', border: '#BDE9E4' };
    return { bg: '#F5F8FA', text: '#516F90', border: '#DFE3EB' };
  }

  function estadoStatusStyle(estado) {
    const tone = estadoTone(estado);
    return { background: tone.bg, color: tone.text, borderColor: tone.border };
  }

  const drawerPanelStyle = isMobile
    ? { width: '100vw', minWidth: 0, maxWidth: '100vw' }
    : { width: '46vw', minWidth: 520, maxWidth: 760 };
  const historialPanelStyle = isMobile
    ? { width: '100vw', minWidth: 0, maxWidth: '100vw' }
    : { width: '50vw', minWidth: 560, maxWidth: 860 };

  const openHistorialGlobal = () => {
    setHistorialGlobalDni('');
    setHistorialGlobalPacienteSearch('');
    setHistorialGlobalSearch('');
    setHistorialGlobalDesde('');
    setHistorialGlobalHasta('');
    setHistorialGlobalOrder('fecha_desc');
    setHistorialGlobalPage(1);
    setHistorialGlobalOpen(true);
  };

  function formatFechaDisplay(fechaRaw) {
    if (!fechaRaw) return '';
    const d = parsePedidoFecha(fechaRaw);
    if (!d) return String(fechaRaw);
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const day = d.getDate();
    const month = months[d.getMonth()] || '';
    const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day} de ${monthCap} · ${hh}:${mm}`;
  }
  function formatFechaSoloDia(fechaRaw) {
    if (!fechaRaw) return '—';
    const d = parsePedidoFecha(fechaRaw);
    if (!d) return String(fechaRaw);
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const day = d.getDate();
    const month = months[d.getMonth()] || '';
    const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
    return `${day} de ${monthCap}`;
  }

  function formatFechaBadge(fechaRaw) {
    if (!fechaRaw) return null;
    const d = parsePedidoFecha(fechaRaw);
    if (!d) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return { dayMonth: `${dd}/${mm}`, time: `${hh}:${min}` };
  }

  function normalizePacienteNombre(raw) {
    const base = String(raw || '').trim();
    if (!base) return '';
    return base
      .replace(/,+/g, ',')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\s{2,}/g, ' ')
      .replace(/,\s*$/, '')
      .trim();
  }

  function getPedidoDni(p) {
    return String(
      p?.cliente_dni
      || p?.paciente_dni
      || p?.dni
      || p?.dni_paciente
      || p?.solicitante_dni
      || ''
    ).trim();
  }

  function formatDni(dniRaw) {
    const digits = String(dniRaw || '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function getPedidoPacienteDisplay(p) {
    const rawName =
      p?.paciente
      || p?.paciente_nombre
      || p?.solicitante
      || p?.nombre_completo
      || [p?.apellido, p?.nombre].filter(Boolean).join(', ')
      || '';
    const cleaned = normalizePacienteNombre(rawName);
    const dni = getPedidoDni(p);
    if (cleaned) return cleaned;
    if (dni) return `DNI ${dni}`;
    return 'Pedido de dispensa';
  }

  function formatMedicamentoLabel(rawName) {
    const name = String(rawName || '').trim();
    if (!name) return '—';
    return name
      .split(' ')
      .filter(Boolean)
      .map((word) => {
        if (word.length <= 1) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  function getMedicamentoCompleto(pedido, { includeLab = true } = {}) {
    const generic = String(pedido?.nombre_generico || pedido?.nombre_generico_med || '').trim();
    const commercial = String(pedido?.nombre_comercial || '').trim();
    const concentration = String(pedido?.concentracion || '').trim();
    const presentacion = String(pedido?.presentacion || '').trim();
    const laboratorio = String(pedido?.laboratorio || '').trim();
    const base = generic || commercial || String(pedido?.medicamento || '').trim();
    if (!base) return '';

    const concentracionYaEnPresentacion = (() => {
      if (!concentration || !presentacion) return false;
      if (presentacion.toLowerCase() === concentration.toLowerCase()) return true;
      const esc = concentration.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`^${esc}(\\s|mg|mcg|g|$)`, 'i').test(presentacion.trim());
    })();

    const parts = [base];
    if (concentration && (!presentacion || !concentracionYaEnPresentacion)) {
      parts.push(concentration);
    }
    if (presentacion) parts.push(presentacion);
    if (includeLab && laboratorio) parts.push(laboratorio);
    return parts.join(' · ');
  }
  function abrirInfoPendienteAnterior(pedido) {
    if (!pedido) return;
    setPendienteInfoPedido(pedido);
    setPendienteInfoDrawerOpen(true);
  }

  function getPedidoConversationId(pedido) {
    return String(pedido?.conversation_id || pedido?.ingreso_conversation_id || '').trim() || null;
  }

  function getPedidoNotasDisplay(pedido) {
    const display = pedido?.notas_display;
    const fromDisplay = display != null ? String(display).trim() : '';
    const raw = fromDisplay || String(pedido?.notas || '').trim().replace(/^\[\[origen:[^\]]+\]\]\s*/i, '').trim();
    return stripPendienteSystemCopy(raw);
  }

  function getPedidoNotasPreview(pedido, maxLen = 72) {
    const text = getPedidoNotasDisplay(pedido);
    if (!text) return '';
    if (text.length <= maxLen) return text;
    return `${text.slice(0, maxLen - 1)}…`;
  }

  function abrirEditarPendiente(pedido) {
    if (!pedido) return;
    setPendienteEditPedido(pedido);
    setPendienteEditDni(getPedidoDni(pedido));
    setPendienteEditDireccion(String(pedido.direccion || '').trim());
    setPendienteEditNotas(getPedidoNotasDisplay(pedido));
    setPendienteEditDrawerOpen(true);
  }

  function irAConversacionPedido(pedido) {
    const convId = getPedidoConversationId(pedido);
    if (!convId) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'warning',
        title: 'Sin conversación vinculada',
        text: 'Podés editar los datos del pedido o buscar al paciente en Conversaciones.',
        showConfirmButton: false,
        timer: 3200,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      abrirEditarPendiente(pedido);
      return;
    }
    try {
      sessionStorage.setItem(OPEN_CONVERSATION_KEY, convId);
    } catch (_) {
      // ignore
    }
    navigate('/conversaciones');
  }

  function handlePendienteCardClick(event, pedido) {
    if (event.target.closest('button, a, input, textarea, select, label')) return;
    abrirEditarPendiente(pedido);
  }

  async function guardarEdicionPendiente() {
    if (!pendienteEditPedido) return;
    const dni = String(pendienteEditDni || '').trim();
    const direccion = String(pendienteEditDireccion || '').trim();
    const notas = String(pendienteEditNotas || '').trim();
    if (!dni) {
      Swal.fire({ icon: 'error', title: 'Ingresá el DNI del paciente' });
      return;
    }
    if (!direccion) {
      Swal.fire({ icon: 'error', title: 'Ingresá la dirección de entrega' });
      return;
    }
    setPendienteEditSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pendienteEditPedido.id}/datos`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_dni: dni,
          direccion_entrega: direccion,
          notas
        })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'No se pudo guardar');
      }
      const updated = await r.json();
      setData((d) => {
        const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
        const idx = list.findIndex((p) => String(p.id) === String(updated.id));
        if (idx === -1) return { ...d, pedidos_farmacia: [updated, ...list] };
        const rest = list.filter((_, i) => i !== idx);
        return { ...d, pedidos_farmacia: [{ ...list[idx], ...updated }, ...rest] };
      });
      setPendienteEditDrawerOpen(false);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Datos actualizados',
        showConfirmButton: false,
        timer: 1800,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error al guardar', text: e.message || String(e) });
    } finally {
      setPendienteEditSaving(false);
    }
  }

  function hasMeaningfulValue(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value) return false;
    return value !== '0';
  }

  function abrirFarmacia(id) {
    const p = pedidosFarm.find(x => x.id === id);
    setSelectedFarmId(id);
    setFarmNro(`FAR-${new Date().getFullYear()}-${String(id).padStart(3, '0')}`);
    setFarmNotas('');
    setModalFarmaciaOpen(true);
  }

  async function confirmarPedidoFarmacia() {
    const nro = String(farmNro || '').trim();
    const notas = String(farmNotas || '').trim();
    if (!nro) {
      Swal.fire({ icon: 'error', title: 'Ingresá el número de pedido' });
      return;
    }
    if (!notas) {
      Swal.fire({ icon: 'error', title: 'Ingresá las notas del pedido' });
      return;
    }

    const pedido = pedidosFarm.find((x) => String(x.id) === String(selectedFarmId));
    const pacienteLabel = pedido ? getPedidoPacienteDisplay(pedido) : 'el paciente';
    const medLabel = pedido ? formatMedicamentoLabel(getMedicamentoCompleto(pedido, { includeLab: false })) : '';
    const labLabel = pedido ? String(pedido.laboratorio || '').trim() : '';
    const medDetailHtml = medLabel
      ? `<dl class="crm-swal-details"><div class="crm-swal-detail"><dt>Medicamento</dt><dd><span>${medLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>${labLabel ? `<span class="crm-farm-lab-badge">🏭 ${labLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>` : ''}</dd></div></dl>`
      : '';

    setModalFarmaciaOpen(false);

    const confirmResult = await fireConfirmDialog({
      title: 'Generar pedido en farmacia',
      html: buildCrmSwalBody({
        lead: `Se registrará el pedido de farmacia para <strong>${pacienteLabel}</strong>.`,
        details: [
          { label: 'N° pedido farmacia', value: nro },
          { label: 'Notas', value: notas },
        ],
        items: ['El pedido pasará a estado En farmacia'],
      }) + medDetailHtml,
      icon: 'question',
      confirmButtonText: 'Generar pedido',
      cancelButtonText: 'Volver',
      allowOutsideClick: false,
      zIndex: 4000,
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '4000';
        const popup = Swal.getPopup();
        if (popup) popup.style.zIndex = '4001';
      },
    });

    if (!confirmResult.isConfirmed) {
      setModalFarmaciaOpen(true);
      return;
    }

    try {
      const body = {
        nro_pedido_farmacia: nro,
        notas,
        direccion_entrega: '',
      };
      if (typeof selectedFarmId === 'string' && selectedFarmId.startsWith('receta-')) {
        const parts = selectedFarmId.split('-');
        const rid = Number(parts[1]);
        if (Number.isFinite(rid)) body.receta_id = rid;
      } else {
        const pid = Number(selectedFarmId);
        if (Number.isFinite(pid)) body.pedido_id = pid;
      }
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const created = await r.json();
        setData((d) => {
          const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
          const matchIndex = list.findIndex((p) => (
            String(p.id) === String(created.id)
            || (created.receta_id && String(p.receta_id) === String(created.receta_id))
            || (created.nro_pedido && String(p.nro_pedido) === String(created.nro_pedido))
            || String(p.id) === String(selectedFarmId)
          ));
          let finalIndex = matchIndex;
          if (finalIndex === -1) {
            finalIndex = list.findIndex((p) => (
              (created.receta_id && String(p.receta_id) === String(created.receta_id))
              || (created.nro_pedido && String(p.nro_pedido) === String(created.nro_pedido))
              || (
                p.paciente && created.paciente
                && String(p.paciente) === String(created.paciente)
                && String(p.fecha) === String(created.fecha)
                && String(p.medicamento) === String(created.medicamento)
              )
            ));
          }
          if (finalIndex !== -1) {
            const existing = list[finalIndex] || {};
            const merged = {
              ...existing,
              ...created,
              paciente: existing.paciente || created.paciente,
              medicamento: existing.medicamento || created.medicamento,
              direccion: existing.direccion || created.direccion,
            };
            const rest = list.filter((_, i) => i !== finalIndex);
            return { ...d, pedidos_farmacia: [merged, ...rest] };
          }
          return { ...d, pedidos_farmacia: [created, ...list] };
        });
        Swal.fire({
          toast: true,
          position: 'bottom-end',
          icon: 'success',
          title: 'Pedido generado en farmacia',
          showConfirmButton: false,
          timer: 3000,
          customClass: { popup: 'swal-toast-crm' },
        });
      } else {
        const err = await r.json().catch(() => ({}));
        Swal.fire({ icon: 'error', title: 'Error al crear pedido', text: err.message || 'Error desconocido' });
        setModalFarmaciaOpen(true);
      }
    } catch (e) {
      console.error('Error al crear pedido farmacia:', e);
      Swal.fire({ icon: 'error', title: 'Error al crear pedido', text: e.message || String(e) });
      setModalFarmaciaOpen(true);
    }
  }

  function abrirEstado(id) {
    const p = pedidosFarm.find(x => x.id === id);
    setSelectedFarmId(id);
    setNuevoEstado(p?.estado || 'pedido_generado_farmacia');
    setModalEstadoOpen(true);
  }

  function abrirMapaGrande({ address, latitud, longitud, paciente, medicamento, laboratorio, nroPedido }) {
    setMapDrawerAddress(String(address || ''));
    setMapDrawerLatitud(Number.isFinite(Number(latitud)) ? Number(latitud) : null);
    setMapDrawerLongitud(Number.isFinite(Number(longitud)) ? Number(longitud) : null);
    setMapDrawerPaciente(String(paciente || ''));
    setMapDrawerMedicamento(String(medicamento || ''));
    setMapDrawerLaboratorio(String(laboratorio || ''));
    setMapDrawerNroPedido(String(nroPedido || '—'));
    setMapDrawerOpen(true);
  }

  function confirmarEstado() {
    (async () => {
      try {
        if (nuevoEstado === 'entregado' || nuevoEstado === 'cancelado') {
          const isCancel = nuevoEstado === 'cancelado';
          const confirm = await fireConfirmDialog({
            title: isCancel ? 'Cancelar pedido' : 'Marcar entregado',
            html: buildCrmSwalBody({
              lead: isCancel
                ? 'El pedido quedará cancelado.'
                : 'El pedido quedará marcado como entregado.',
              items: isCancel
                ? ['No se puede deshacer desde este panel']
                : ['Se actualiza el estado en farmacia']
            }),
            icon: isCancel ? 'warning' : 'question',
            confirmButtonText: isCancel ? 'Cancelar pedido' : 'Marcar entregado',
            cancelButtonText: 'Volver',
            allowOutsideClick: false,
            zIndex: 4000,
            didOpen: () => {
              const container = Swal.getContainer();
              if (container) container.style.zIndex = '4000';
              const popup = Swal.getPopup();
              if (popup) popup.style.zIndex = '4001';
            }
          });
          if (!confirm.isConfirmed) return;
        }

        // resolve numeric pedido_farmacia id
        let pid = null;
        if (Number.isFinite(Number(selectedFarmId))) pid = Number(selectedFarmId);
        else if (typeof selectedFarmId === 'string' && selectedFarmId.startsWith('receta-')) {
          const parts = selectedFarmId.split('-');
          const rid = Number(parts[1]);
          const found = (data.pedidos_farmacia || []).find(p => String(p.receta_id) === String(rid) || String(p.id) === String(selectedFarmId));
          if (found && Number.isFinite(Number(found.id))) pid = Number(found.id);
        }
        if (!pid) {
          // fallback: try to find selected by id string in local list
          const fallback = (data.pedidos_farmacia || []).find(p => String(p.id) === String(selectedFarmId) || String(p.receta_id) === String(selectedFarmId));
          if (fallback && Number.isFinite(Number(fallback.id))) pid = Number(fallback.id);
        }
        if (!pid) {
          Swal.fire({ icon: 'error', title: 'No se encontró pedido para actualizar' });
          return;
        }

        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/estado`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_estado: nuevoEstado, origen: 'call_center' })
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: err.message || 'Error desconocido' });
          return;
        }
        const updated = await r.json();
        setData(d => {
          const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
          const matchIndex = list.findIndex(p => String(p.id) === String(updated.id) || (updated.receta_id && String(p.receta_id) === String(updated.receta_id)) || (updated.nro_pedido && String(p.nro_pedido) === String(updated.nro_pedido)));
          let finalIndex = matchIndex;
          if (finalIndex === -1) {
            finalIndex = list.findIndex(p => (
              (updated.receta_id && String(p.receta_id) === String(updated.receta_id)) ||
              (updated.nro_pedido && String(p.nro_pedido) === String(updated.nro_pedido)) ||
              (p.paciente && updated.paciente && String(p.paciente) === String(updated.paciente) && String(p.fecha) === String(updated.fecha) && String(p.medicamento) === String(updated.medicamento))
            ));
          }
          if (finalIndex !== -1) {
            const rest = list.filter((_, i) => i !== finalIndex);
            const merged = { ...list[finalIndex], ...updated };
            return { ...d, pedidos_farmacia: [merged, ...rest] };
          }
          // try to remove any synthetic entries matching by receta_id, nro_pedido or the selectedFarmId to avoid duplicates
          const filtered = list.filter(p => (
            !(updated.receta_id && String(p.receta_id) === String(updated.receta_id))
            && !(updated.nro_pedido && String(p.nro_pedido) === String(updated.nro_pedido))
            && !(selectedFarmId && String(p.id) === String(selectedFarmId))
          ));
          return { ...d, pedidos_farmacia: [updated, ...filtered] };
        });
        setModalEstadoOpen(false);
        Swal.fire({ toast: true, position: 'bottom-end', icon: 'info', title: 'Estado actualizado', showConfirmButton: false, timer: 2200, customClass: { popup: 'swal-toast-crm' } });
      } catch (e) {
        console.error('Error actualizando estado:', e);
        Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: e.message || String(e) });
      }
    })();
  }

  function canMoveEstado(currentEstado, targetEstado) {
    const flow = ['pendiente_call_center', 'pedido_generado_farmacia', 'en_preparacion', 'en_camino'];
    if (currentEstado === targetEstado) return false;
    const cIdx = flow.indexOf(String(currentEstado || ''));
    const tIdx = flow.indexOf(String(targetEstado || ''));
    if (cIdx === -1 || tIdx === -1) return false;
    return Math.abs(tIdx - cIdx) === 1;
  }

  function isFarmaciaKanbanTransition(estadoActual, targetEstado) {
    return getFarmaciaAllowedTransitions(String(estadoActual || '')).includes(String(targetEstado || ''));
  }

  async function actualizarEstadoPedidoFarmacia(pedido, estadoDestino, origen = 'farmacia_kanban') {
    const estadoActual = String(pedido?.estado || '');
    const destino = String(estadoDestino || '');
    if (!isFarmaciaKanbanTransition(estadoActual, destino)) {
      const currentIdx = FARMACIA_ESTADO_FLOW.indexOf(estadoActual);
      const targetIdx = FARMACIA_ESTADO_FLOW.indexOf(destino);
      let text = 'Solo podés mover el pedido entre estados permitidos.';
      if (currentIdx >= 0 && targetIdx >= 0 && targetIdx > currentIdx + 1) {
        const siguiente = getFarmaciaSiguienteEstado(estadoActual);
        text = `No se puede saltar estados. Debes pasar primero por "${estadoLabel(siguiente)}".`;
      }
      await fireCrmAlert({
        title: 'Transición no permitida',
        icon: 'warning',
        html: buildCrmSwalBody({
          lead: text,
          details: [
            { label: 'Estado actual', value: estadoLabel(estadoActual) },
            { label: 'Destino', value: estadoLabel(destino) }
          ],
          items: ['Seguí el orden del flujo: En farmacia → En preparación → Entregado al cadete.']
        })
      });
      return false;
    }
    try {
      if (isAtencionFarmaciaPedido(pedido)) {
        const apid = resolveAtencionPedidoId(pedido);
        const updated = await updateAtencionPedidoFarmaciaEstado(apid, destino, origen);
        setData((d) => {
          const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
          return {
            ...d,
            pedidos_farmacia: list.map((p) => (String(p.id) === String(pedido.id) ? { ...p, ...updated, estado: updated.estado } : p))
          };
        });
        return true;
      }
      const pid = Number(pedido?.id);
      if (!Number.isFinite(pid)) return false;
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/estado`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_estado: destino, origen })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: err.message || 'Error desconocido' });
        return false;
      }
      const updated = await r.json();
      setData((d) => {
        const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
        const idx = list.findIndex((p) => String(p.id) === String(updated.id));
        if (idx !== -1) {
          const rest = list.filter((_, i) => i !== idx);
          return { ...d, pedidos_farmacia: [{ ...list[idx], ...updated }, ...rest] };
        }
        return { ...d, pedidos_farmacia: [updated, ...list] };
      });
      return true;
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: e.message || String(e) });
      return false;
    }
  }

  async function solicitarConfirmacionCambioEstadoFarmacia(pedido, estadoDestino, origen = 'farmacia_kanban') {
    const estadoActual = String(pedido?.estado || '');
    const destino = String(estadoDestino || '');
    if (!isFarmaciaKanbanTransition(estadoActual, destino)) {
      return actualizarEstadoPedidoFarmacia(pedido, destino, origen);
    }
    const confirmResult = await fireConfirmDialog({
      title: 'Cambiar estado del pedido',
      html: buildCrmSwalBody({
        lead: `¿Pasamos el pedido de <strong>${pedido?.paciente || 'este paciente'}</strong> a <strong>${estadoLabel(destino)}</strong>?`,
        details: [
          { label: 'Estado actual', value: estadoLabel(estadoActual) },
          { label: 'Nuevo estado', value: estadoLabel(destino) },
          ...(pedido?.nro_pedido ? [{ label: 'N° pedido', value: String(pedido.nro_pedido) }] : [])
        ],
        items: ['Quedará registrado quién realizó el cambio']
      }),
      icon: 'question',
      confirmButtonText: 'Confirmar cambio',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      zIndex: 4000,
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '4000';
        const popup = Swal.getPopup();
        if (popup) popup.style.zIndex = '4001';
      }
    });
    if (!confirmResult.isConfirmed) return false;
    return actualizarEstadoPedidoFarmacia(pedido, destino, origen);
  }

  function abrirNotasPedido(pedido) {
    if (!hasPedidoNotasFarmacia(pedido)) return;
    setNotasPopupPedido(pedido);
  }

  async function abrirDetallePedido(pedido) {
    const fallbackPedido = normalizeAtencionFarmaciaCard(pedido);
    setPedidoDetailOpen(true);
    setPedidoDetailLoading(true);
    setPedidoDetailError('');
    setPedidoDetailData(fallbackPedido);
    try {
      const pedidoId = resolveAtencionPedidoId(fallbackPedido);
      if (!Number.isFinite(pedidoId) || pedidoId <= 0) {
        throw new Error('No pude resolver el pedido para mostrar el detalle.');
      }
      const fetchedPedido = await fetchAtencionPedido(pedidoId);
      const normalized = normalizeAtencionFarmaciaCard(fetchedPedido || fallbackPedido);
      setPedidoDetailData(normalized);
    } catch (error) {
      setPedidoDetailData(fallbackPedido);
      setPedidoDetailError(error?.message || 'No pude cargar el detalle completo del pedido.');
    } finally {
      setPedidoDetailLoading(false);
    }
  }

  async function abrirTrazabilidadPedido(pedido) {
    if (isAtencionFarmaciaPedido(pedido)) {
      setTrazabilidadPedido(pedido);
      setTrazabilidadItems([
        {
          new_estado: pedido.estado || 'pedido_generado_farmacia',
          previous_estado: null,
          origen: 'bot_mostrador',
          notas_cambio: 'Pedido ingresado desde Bot Farmacia (mostrador)',
          created_at: pedido.fecha || null
        }
      ]);
      setTrazabilidadLoading(false);
      return;
    }
    const pid = Number(pedido?.id);
    if (!Number.isFinite(pid)) return;
    setTrazabilidadPedido(pedido);
    setTrazabilidadItems([]);
    setTrazabilidadLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/hist`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const histData = await r.json();
      setTrazabilidadItems(Array.isArray(histData.history) ? histData.history : []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cargar la trazabilidad', text: e.message || String(e) });
      setTrazabilidadPedido(null);
    } finally {
      setTrazabilidadLoading(false);
    }
  }

  async function revertirPedidoASinGestion(pedido) {
    const confirmResult = await fireConfirmDialog({
      title: 'Volver a Sin gestión',
      html: buildCrmSwalBody({
        lead: `¿Devolvemos el pedido de <strong>${pedido?.paciente || 'este paciente'}</strong> a <strong>Sin gestión</strong>?`,
        details: [
          { label: 'Estado actual', value: estadoLabel(pedido?.estado) },
          { label: 'Nuevo estado', value: 'Sin gestión' },
          ...(pedido?.nro_pedido ? [{ label: 'N° pedido', value: String(pedido.nro_pedido) }] : [])
        ],
        items: ['Solo aplica a pedidos del bot de crónicas']
      }),
      icon: 'question',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false
    });
    if (!confirmResult.isConfirmed) return false;
    try {
      const pid = Number(pedido?.id);
      if (!Number.isFinite(pid)) return false;
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/estado`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_estado: 'pendiente_call_center', origen: 'call_center' })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: err.message || 'Error desconocido' });
        return false;
      }
      const updated = await r.json();
      setData((d) => {
        const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
        const idx = list.findIndex((p) => String(p.id) === String(updated.id));
        if (idx !== -1) {
          const rest = list.filter((_, i) => i !== idx);
          return { ...d, pedidos_farmacia: [{ ...list[idx], ...updated, estado: 'pendiente_call_center' }, ...rest] };
        }
        return { ...d, pedidos_farmacia: [{ ...updated, estado: 'pendiente_call_center' }, ...list] };
      });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title: 'Devuelto a Sin gestión', showConfirmButton: false, timer: 1700, customClass: { popup: 'swal-toast-crm' } });
      return true;
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: e.message || String(e) });
      return false;
    }
  }

  async function moverEstadoDirecto(pedido, targetEstado) {
    if (!pedido) return false;
    if (pedido.estado === 'pendiente_call_center' && targetEstado === 'pedido_generado_farmacia') {
      abrirFarmacia(pedido.id);
      return false;
    }
    if (pedido.estado === 'pedido_generado_farmacia' && targetEstado === 'pendiente_call_center') {
      if (!canRevertirPedidoASinGestion(pedido)) {
        const pacienteLabel = getPedidoPacienteDisplay(pedido);
        const esMostrador = isBotMostradorPedido(pedido);
        await fireCrmAlert({
          title: 'No se puede devolver a Sin gestión',
          icon: 'warning',
          html: buildCrmSwalBody({
            lead: esMostrador
              ? `El pedido de <strong>${pacienteLabel}</strong> ingresó por <strong>Bot mostrador</strong> y ya está en el flujo de farmacia.`
              : `El pedido de <strong>${pacienteLabel}</strong> no puede volver a <strong>Sin gestión</strong> desde En farmacia.`,
            details: [
              { label: 'Estado actual', value: estadoLabel(pedido.estado) },
              ...(esMostrador ? [{ label: 'Origen', value: 'Bot mostrador' }] : []),
              ...(pedido?.nro_pedido ? [{ label: 'N° pedido', value: String(pedido.nro_pedido) }] : [])
            ],
            items: esMostrador
              ? [
                'Los pedidos de mostrador avanzan por En farmacia → En preparación → Entregado al cadete.',
                'Para ajustar datos, usá las acciones de la tarjeta en el estado actual.'
              ]
              : ['Solo los pedidos de Bot recetas crónicas pueden revertirse a Sin gestión desde En farmacia.'],
            footnote: esMostrador
              ? 'Si el pedido fue creado por error, cancelalo desde el flujo correspondiente o consultá con operaciones.'
              : 'Si necesitás corregir el pedido, contactá al equipo de operaciones.'
          })
        });
        return false;
      }
      return revertirPedidoASinGestion(pedido);
    }
    if (isFarmaciaKanbanTransition(pedido.estado, targetEstado)) {
      const ok = await solicitarConfirmacionCambioEstadoFarmacia(pedido, targetEstado, 'farmacia_kanban');
      if (ok) {
        Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title: `Movido a ${estadoLabel(targetEstado)}`, showConfirmButton: false, timer: 1700, customClass: { popup: 'swal-toast-crm' } });
      }
      return ok;
    }
    if (!canMoveEstado(pedido.estado, targetEstado)) {
      await fireCrmAlert({
        title: 'Movimiento no permitido',
        icon: 'warning',
        html: buildCrmSwalBody({
          lead: 'Solo podés mover el pedido a un <strong>estado contiguo</strong> en el tablero.',
          details: [
            { label: 'Estado actual', value: estadoLabel(pedido.estado) },
            { label: 'Destino', value: estadoLabel(targetEstado) }
          ],
          items: ['Arrastrá la tarjeta a la columna inmediata anterior o siguiente.']
        })
      });
      return false;
    }
    try {
      const pid = Number(pedido.id);
      if (!Number.isFinite(pid)) return false;
      const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/estado`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_estado: targetEstado, origen: 'call_center' })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: err.message || 'Error desconocido' });
        return false;
      }
      const updated = await r.json();
      setData(d => {
        const list = Array.isArray(d.pedidos_farmacia) ? d.pedidos_farmacia.slice() : [];
        const idx = list.findIndex(p => String(p.id) === String(updated.id));
        if (idx !== -1) {
          const rest = list.filter((_, i) => i !== idx);
          return { ...d, pedidos_farmacia: [{ ...list[idx], ...updated }, ...rest] };
        }
        return { ...d, pedidos_farmacia: [updated, ...list] };
      });
      Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title: `Movido a ${estadoLabel(targetEstado)}`, showConfirmButton: false, timer: 1700, customClass: { popup: 'swal-toast-crm' } });
      return true;
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error actualizando estado', text: e.message || String(e) });
      return false;
    }
  }


  return (
    <section className="crm-gd-page crm-farm-page crm-dispensa-page crm-pbody">
      <div className="crm-dispensa-chrome">
        <div className="crm-gd-kpi-grid crm-farm-kpi-grid crm-farm-kpi-grid--dispensa crm-farm-kpi-strip--dispensa">
          <div className="crm-gd-kpi-card crm-farm-kpi-card--pendiente">
            <div className="crm-gd-kpi-card__num">{kpiSinGestion}</div>
            <div className="crm-gd-kpi-card__label">Sin gestión</div>
          </div>
          <div className="crm-gd-kpi-card crm-farm-kpi-card--ingresado">
            <div className="crm-gd-kpi-card__num">{kpiEnFarmacia}</div>
            <div className="crm-gd-kpi-card__label">En farmacia</div>
          </div>
          <div className="crm-gd-kpi-card crm-farm-kpi-card--preparacion">
            <div className="crm-gd-kpi-card__num">{kpiEnPreparacion}</div>
            <div className="crm-gd-kpi-card__label">En preparación</div>
          </div>
          <div className="crm-gd-kpi-card crm-farm-kpi-card--camino">
            <div className="crm-gd-kpi-card__num">{kpiEntregadoCadete}</div>
            <div className="crm-gd-kpi-card__label">Entregado al cadete</div>
          </div>
          <div className="crm-gd-kpi-card crm-farm-kpi-card--finalizados">
            <div className="crm-gd-kpi-card__num">{kpiFinalizados}</div>
            <div className="crm-gd-kpi-card__label">Finalizados</div>
          </div>
        </div>

        {focusedPedido ? (
          <div className="crm-farm-bot-focus-banner" role="status" aria-live="polite">
            <span className="crm-farm-bot-focus-banner__glow" aria-hidden />
            <span className="crm-farm-bot-focus-banner__icon" aria-hidden>🤖</span>
            <div className="crm-farm-bot-focus-banner__copy">
              <strong>Pedido desde Bot Farmacia</strong>
              <span>
                {focusedPedido.nro_pedido || `AT-${resolveAtencionPedidoId(focusedPedido) || '—'}`}
                {' · '}
                {estadoLabel(focusedPedido.estado)}
                {' · '}
                {getPedidoPacienteDisplay(focusedPedido)}
              </span>
            </div>
            <div className="crm-farm-bot-focus-banner__actions">
              <Link
                to={focusedPedido.conversation_id
                  ? `/bot-farmacia?conv=${encodeURIComponent(focusedPedido.conversation_id)}`
                  : '/bot-farmacia'}
                className="crm-farm-bot-focus-banner__link"
              >
                Volver al bot
              </Link>
              <button
                type="button"
                className="crm-farm-bot-focus-banner__dismiss"
                onClick={clearFarmaciaFocus}
              >
                Quitar foco
              </button>
            </div>
          </div>
        ) : null}

        <div className="crm-farm-filters">
          <div className="crm-farm-filters__search-row">
            <input
              type="search"
              className="crm-gd-input crm-farm-filters__search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar paciente, medicamento o n° pedido"
              aria-label="Buscar pedidos"
            />
            {/* <button type="button" className="crm-gd-btn crm-gd-btn--ghost crm-farm-filters__hist" onClick={openHistorialGlobal}>
            Historial
          </button> */}
          </div>
          <div className="crm-farm-filters__controls">
            <div className="crm-farm-segment" role="group" aria-label="Vista">
              <button
                type="button"
                className={`crm-farm-segment__btn${filtroRangoModo === 'solo_hoy' ? ' is-active' : ''}`}
                onClick={() => { setFiltroRangoModo('solo_hoy'); setPage(1); }}
              >
                Hoy
              </button>
              <button
                type="button"
                className={`crm-farm-segment__btn crm-farm-segment__btn--backlog${filtroRangoModo === 'hoy_anteriores' ? ' is-active' : ''}${backlogPendienteCount > 0 && filtroRangoModo !== 'hoy_anteriores' ? ' has-backlog-alert' : ''}`}
                onClick={() => { setFiltroRangoModo('hoy_anteriores'); setPage(1); }}
                title={
                  backlogPendienteCount > 0
                    ? `${backlogPendienteCount} pedido${backlogPendienteCount === 1 ? '' : 's'} pendiente${backlogPendienteCount === 1 ? '' : 's'} en backlog`
                    : 'Sin pendientes en backlog'
                }
              >
                Backlog
                {filtroRangoModo !== 'hoy_anteriores' ? (
                  <span
                    className={`crm-farm-segment__badge${backlogPendienteCount > 0 ? ' crm-farm-segment__badge--alert' : ' crm-farm-segment__badge--empty'}`}
                    aria-label={`${backlogPendienteCount} pendiente${backlogPendienteCount === 1 ? '' : 's'} en backlog`}
                  >
                    {backlogPendienteCount}
                  </span>
                ) : null}
              </button>
            </div>
            <div className="crm-farm-date-range">
              <label className="crm-farm-date-range__field">
                <span className="crm-farm-date-range__lbl">Desde</span>
                <input type="date" className="crm-gd-input" value={filtroDesde} onChange={(e) => { setFiltroDesde(e.target.value); setPage(1); }} />
              </label>
              <label className="crm-farm-date-range__field">
                <span className="crm-farm-date-range__lbl">Hasta</span>
                <input type="date" className="crm-gd-input" value={filtroHasta} onChange={(e) => { setFiltroHasta(e.target.value); setPage(1); }} />
              </label>
            </div>
            <div className="crm-farm-extra-filters" role="group" aria-label="Filtros por origen del bot">
              <label className="crm-farm-extra-filters__field">
                <span className="crm-farm-date-range__lbl">Filtro</span>
                <select
                  className="crm-gd-input"
                  value={filtroBotOrigen}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFiltroBotOrigen(next);
                    if (next !== 'farmacia') {
                      setFiltroBotEntrega('todos');
                      setFiltroBotSucursal('todos');
                    }
                  }}
                >
                  <option value="todos">Todos</option>
                  <option value="cronicas">Recetas crónicas</option>
                  <option value="farmacia">Farmacia</option>
                </select>
              </label>
              {filtroBotOrigen === 'farmacia' ? (
                <label className="crm-farm-extra-filters__field">
                  <span className="crm-farm-date-range__lbl">Entrega</span>
                  <select
                    className="crm-gd-input"
                    value={filtroBotEntrega}
                    onChange={(e) => setFiltroBotEntrega(e.target.value)}
                  >
                    <option value="todos">Todos</option>
                    <option value="mostrador">Entrega en mostrador</option>
                    <option value="domicilio">Envío a domicilio</option>
                  </select>
                </label>
              ) : null}
              {filtroBotOrigen === 'farmacia' ? (
                <label className="crm-farm-extra-filters__field">
                  <span className="crm-farm-date-range__lbl">Sucursal</span>
                  <select
                    className="crm-gd-input"
                    value={filtroBotSucursal}
                    onChange={(e) => setFiltroBotSucursal(e.target.value)}
                  >
                    <option value="todos">Todas</option>
                    <option value="sin_asignar">Sin asignar</option>
                    {sucursalFilterOptions.map((sucursal) => (
                      <option key={sucursal.codigo} value={sucursal.codigo}>
                        {sucursal.nombre}
                        {/* {sucursal.nombre}{sucursal.localidad ? ` · ${sucursal.localidad}` : ''} */}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            {/* <button
              type="button"
              className="crm-gd-btn crm-farm-filters__clear"
              onClick={() => {
                setSearch('');
                setFiltroDesde('');
                setFiltroHasta('');
                setFiltroRangoModo('solo_hoy');
                setFiltroBotOrigen('todos');
                setFiltroBotEntrega('todos');
                setPage(1);
              }}
            >
              Limpiar
            </button> */}
          </div>
          {totalKanbanPages > 1 ? (
            <div className="crm-farm-board-toolbar__pager">
              <button
                type="button"
                className="crm-farm-board-toolbar__pager-btn"
                disabled={kanbanPage <= 1}
                onClick={() => setKanbanPage((current) => Math.max(1, current - 1))}
                aria-label="Página anterior del tablero"
              >
                ◀
              </button>
              <span className="crm-farm-board-toolbar__pager-page">{kanbanPage}/{totalKanbanPages}</span>
              <button
                type="button"
                className="crm-farm-board-toolbar__pager-btn"
                disabled={kanbanPage >= totalKanbanPages}
                onClick={() => setKanbanPage((current) => Math.min(totalKanbanPages, current + 1))}
                aria-label="Página siguiente del tablero"
              >
                ▶
              </button>
            </div>
          ) : null}
        </div>

        <div className="crm-farm-board-wrap">
          {initialLoading ? (
            <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true">
              <div className="crm-gd-spinner" aria-hidden="true" />
              <div className="crm-gd-loading__title">Preparando Contact Center</div>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Cargando pedidos de Contact Center…</p>
            </div>
          ) : !isMobile ? (
            <>
              <div className="crm-farm-board crm-farm-board--cols-5">
                {DISPENSA_KANBAN_COLUMNS.map((col) => {
                  const cards = col.key === 'finalizados'
                    ? kanbanColumnItems.finalizados.slice((kanbanPage - 1) * KANBAN_BOARD_PAGE_SIZE, kanbanPage * KANBAN_BOARD_PAGE_SIZE)
                    : (kanbanColumnItems[col.key] || []).slice((kanbanPage - 1) * KANBAN_BOARD_PAGE_SIZE, kanbanPage * KANBAN_BOARD_PAGE_SIZE);
                  const totalCards = col.key === 'finalizados'
                    ? kanbanColumnItems.finalizados.length
                    : (kanbanColumnItems[col.key] || []).length;
                  const isOver = dragOverEstado === col.key;
                  const isFinalizadosColumn = col.key === 'finalizados';
                  const isPendienteDerivacionColumn = col.key === 'pendiente_call_center';
                  const isReadOnlyColumn = isDispensaReadonlyColumn(col.key);
                  const readonlyMeta = getDispensaReadonlyMeta(col.key);
                  return (
                    <div
                      key={`disp-board-${col.key}`}
                      className={`crm-farm-kanban-col crm-farm-kanban-col--${col.key.replace(/_/g, '-')}${isFinalizadosColumn ? ' is-finalizados' : ''}${isPendienteDerivacionColumn ? ' is-pendiente-derivacion' : ''}${!isFinalizadosColumn && isReadOnlyColumn ? ' is-readonly' : ''}${isOver ? ' is-over' : ''}`}
                      aria-disabled={isReadOnlyColumn ? true : undefined}
                      title={isReadOnlyColumn ? readonlyMeta.ariaTitle : undefined}
                      onDragOver={(e) => {
                        if (isReadOnlyColumn) return;
                        e.preventDefault();
                        setDragOverEstado(col.key);
                      }}
                      onDragLeave={() => setDragOverEstado((prev) => (prev === col.key ? '' : prev))}
                      onDrop={async (e) => {
                        if (isReadOnlyColumn) return;
                        e.preventDefault();
                        const draggedId = String(e.dataTransfer.getData('text/plain') || draggingPedidoId || '');
                        setDragOverEstado('');
                        setDraggingPedidoId(null);
                        if (!draggedId) return;
                        const pedido = boardItems.find((x) => String(x.id) === draggedId);
                        if (!pedido) return;
                        await moverEstadoDirecto(pedido, col.key);
                      }}
                    >
                      <div className="crm-farm-kanban-col__head">
                        <div>
                          <div className="crm-farm-kanban-col__title">{col.title}</div>
                          {col.subtitle ? (
                            <div className="crm-farm-kanban-col__sub">{col.subtitle}</div>
                          ) : null}
                        </div>
                        <div className="crm-farm-kanban-col__meta">
                          {isFinalizadosColumn ? (
                            <span className="crm-farm-finalizados-col__badge">
                              <span className="crm-farm-finalizados-col__badge-dot" aria-hidden />
                              Solo consulta
                            </span>
                          ) : null}
                          <span className="crm-farm-kanban-col__count">{cards.length}</span>
                        </div>
                      </div>
                      {isFinalizadosColumn ? (
                        <div className="crm-farm-finalizados-banner" role="note">
                          <span className="crm-farm-finalizados-banner__icon" aria-hidden>{readonlyMeta.icon}</span>
                          <div className="crm-farm-finalizados-banner__text">
                            <strong>{readonlyMeta.title}</strong>
                            <span>{readonlyMeta.hint}</span>
                          </div>
                        </div>
                      ) : isPendienteDerivacionColumn ? (
                        <div className="crm-disp-pendiente-banner crm-disp-pendiente-banner--compact" role="note">
                          <span className="crm-disp-pendiente-banner__icon" aria-hidden>🤖</span>
                          <div className="crm-disp-pendiente-banner__text">
                            <strong>Bot recetas crónicas</strong>
                            <span>Arrastrá a En farmacia o editá con ✏️</span>
                          </div>
                        </div>
                      ) : null}
                      <div className="crm-farm-kanban-col__cards">
                        {cards.length === 0 ? (
                          <div className={`crm-farm-kanban-empty${isFinalizadosColumn ? ' crm-farm-kanban-empty--finalizados' : ''}${!isFinalizadosColumn && isReadOnlyColumn ? ' crm-farm-kanban-empty--readonly' : ''}`}>
                            {isReadOnlyColumn ? (
                              <>
                                <span className="crm-farm-kanban-empty__ico" aria-hidden>{isFinalizadosColumn ? '✓' : '📋'}</span>
                                {readonlyMeta.empty}
                                <span className="crm-farm-kanban-empty__hint">&nbsp;{readonlyMeta.emptyHint}</span>
                              </>
                            ) : (
                              'Soltá pedidos aquí'
                            )}
                          </div>
                        ) : cards.map((p) => {
                          const isPendienteAnterior = isPedidoPendienteAnterior(p);
                          const cardDraggable = !isReadOnlyColumn && isDispensaCardDraggable(String(p.estado));
                          const isFinalizadoCard = isFinalizadosColumn && isPedidoEstadoTerminal(p.estado);
                          const isPendienteDerivacionCard = String(p.estado) === 'pendiente_call_center';
                          const finalizadoVariant = getFinalizadoCardVariant(p.estado);
                          const cardReadonly = !isFinalizadoCard && !isPendienteDerivacionCard && !cardDraggable;
                          const notasPreview = getPedidoNotasPreview(p);
                          const fechaPendiente = formatFechaDisplay(p.fecha);
                          const pendienteMedLabel = getPendienteMedicamentoDisplay(p);
                          const pendienteDni = getPedidoDni(p);
                          const isBotFocus = Boolean(focusPedidoKey && pedidoMatchesFarmaciaFocus(p, focusPedidoKey));
                          const origenBadge = getFarmaciaOrigenBadge(p);
                          const isCronicosPedido = isBotTratamientosCronicosPedido(p);
                          const isMostradorPedido = isBotMostradorPedido(p);
                          const isDomicilioPedido = isBotFarmaciaDomicilioPedido(p);
                          const sucursalNombre = getPedidoSucursalNombre(p);
                          const botConversationId = getPedidoConversationId(p);
                          return (
                            <div
                              key={`disp-k-${p.id}`}
                              ref={isBotFocus ? focusCardRef : undefined}
                              draggable={cardDraggable}
                              role={isPendienteDerivacionCard ? 'button' : undefined}
                              tabIndex={isPendienteDerivacionCard ? 0 : undefined}
                              onClick={isPendienteDerivacionCard ? (e) => handlePendienteCardClick(e, p) : undefined}
                              onKeyDown={isPendienteDerivacionCard ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  irAConversacionPedido(p);
                                }
                              } : undefined}
                              onDragStart={(e) => {
                                if (!cardDraggable) return;
                                setDraggingPedidoId(String(p.id));
                                e.dataTransfer.setData('text/plain', String(p.id));
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => {
                                setDraggingPedidoId(null);
                                setDragOverEstado('');
                              }}
                              className={`crm-farm-kanban-card crm-farm-kanban-card--est-${String(p.estado || col.key).replace(/_/g, '-')}${isFinalizadoCard ? ` is-finalizado is-finalizado--${finalizadoVariant}` : ''}${isPendienteDerivacionCard ? ' is-pendiente-derivacion is-pendiente-compact' : ''}${isCronicosPedido && !isPendienteDerivacionCard ? ' has-bot-cronicos' : ''}${isMostradorPedido && !isPendienteDerivacionCard ? ' has-bot-mostrador' : ''}${cardReadonly ? ' is-readonly is-readonly-premium' : ''}${isPendienteAnterior && !cardReadonly && !isFinalizadoCard ? ' is-backlog' : ''}${isBotFocus ? ' is-bot-focus' : ''}`}
                              title={isPendienteDerivacionCard ? 'Editar datos o arrastrar a En farmacia' : undefined}
                            >
                              {isBotFocus ? (
                                <div className="crm-farm-bot-focus-ribbon" aria-hidden>
                                  <span className="crm-farm-bot-focus-ribbon__mark">✓</span>
                                  <span className="crm-farm-bot-focus-ribbon__text">Desde Bot Farmacia</span>
                                </div>
                              ) : null}
                              {isFinalizadoCard ? (
                                <>
                                  <div className="crm-farm-finalizado-ribbon" aria-hidden />
                                  <div className="crm-farm-finalizado-stamp" aria-hidden>
                                    <span className="crm-farm-finalizado-stamp__ring" />
                                    <span className="crm-farm-finalizado-stamp__icon">{finalizadoVariant === 'cancelado' ? '✕' : '✓'}</span>
                                    <span className="crm-farm-finalizado-stamp__label">{getFinalizadoStampLabel(p.estado)}</span>
                                  </div>
                                </>
                              ) : cardReadonly ? (
                                <div className="crm-farm-kanban-card__veil" aria-hidden />
                              ) : null}
                              {isPendienteDerivacionCard ? (
                                <div className="crm-disp-pendiente-actions crm-disp-pendiente-actions--compact">
                                  <button
                                    type="button"
                                    className="crm-disp-pendiente-edit-btn crm-disp-pendiente-edit-btn--compact"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      abrirEditarPendiente(p);
                                    }}
                                    title="Editar datos del pedido"
                                    aria-label="Editar datos del pedido"
                                  >
                                    <span aria-hidden>✏️</span>
                                  </button>
                                </div>
                              ) : null}
                              {isPendienteAnterior && !cardReadonly ? (
                                <button
                                  type="button"
                                  className="crm-farm-backlog-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    abrirInfoPendienteAnterior(p);
                                  }}
                                  title="Ver detalle de pendiente anterior"
                                  aria-label="Ver pendiente anterior"
                                >
                                  i
                                </button>
                              ) : null}
                              <div className="crm-farm-kanban-card__body">
                                {isPendienteDerivacionCard ? (
                                  <div className="crm-disp-pendiente-compact">
                                    <div className="crm-disp-pendiente-compact__head">
                                      <div className="crm-farm-kanban-card__contact">
                                        <div className="crm-farm-kanban-card__patient">{getPedidoPacienteDisplay(p)}</div>
                                        {origenBadge ? (
                                          <span className={`crm-farm-kanban-card__origen crm-farm-kanban-card__origen--${origenBadge.variant}`}>
                                            {origenBadge.label}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                    {(pendienteDni || fechaPendiente) ? (
                                      <div className="crm-disp-pendiente-compact__meta">
                                        {pendienteDni ? <span>DNI {formatDni(pendienteDni)}</span> : null}
                                        {fechaPendiente ? <span>{fechaPendiente}</span> : null}
                                      </div>
                                    ) : null}
                                    <div className="crm-farm-kanban-card__med">{pendienteMedLabel}</div>
                                    {(p.presentacion || p.concentracion) ? (
                                      <div className="crm-farm-kanban-card__presentacion">{String(p.presentacion || p.concentracion)}</div>
                                    ) : null}
                                    {notasPreview ? (
                                      <div className="crm-disp-pendiente-compact__notas" title={getPedidoNotasDisplay(p)}>
                                        {notasPreview}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : (
                                  <>
                                    <div className={`crm-farm-kanban-card__top${cardReadonly ? ' crm-farm-kanban-card__top--readonly' : ''}`}>
                                      <div className="crm-farm-kanban-card__contact">
                                        <div className="crm-farm-kanban-card__patient" title={getPedidoPacienteDisplay(p)}>
                                          {getPedidoPacienteDisplay(p)}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                          {origenBadge || cardReadonly ? (
                                            <div className="crm-disp-card-tags">
                                              {origenBadge ? (
                                                <span className={`crm-farm-kanban-card__origen crm-farm-kanban-card__origen--${origenBadge.variant}`}>
                                                  {origenBadge.label} - {p.nro_pedido ? `N° ${p.nro_pedido}` : 'Sin N° pedido'}
                                                </span>
                                              ) : null}
                                              {cardReadonly ? (
                                                <span className="crm-farm-kanban-card__estado" style={estadoStatusStyle(p.estado)}>
                                                  {estadoLabel(p.estado)}
                                                </span>
                                              ) : null}
                                            </div>
                                          ) : <span />}
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              abrirDetallePedido(p);
                                            }}
                                            title="Ver detalle del pedido"
                                            aria-label="Ver detalle del pedido"
                                            style={{
                                              border: '1px solid rgba(59,130,246,.22)',
                                              background: 'linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)',
                                              color: '#1d4ed8',
                                              borderRadius: 6,
                                              padding: '4px 10px',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 4,
                                              fontWeight: 600,
                                              fontSize: 11,
                                              boxShadow: '0 8px 20px rgba(37,99,235,.10)',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <span aria-hidden>👁</span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="crm-disp-card-med-block">
                                      <div className="crm-farm-kanban-card__med">{formatMedicamentoLabel(p.medicamento || '')}</div>
                                      {(p.presentacion || p.concentracion) ? (
                                        <div className="crm-farm-kanban-card__presentacion">{String(p.presentacion || p.concentracion)}</div>
                                      ) : null}
                                    </div>
                                    {Number.isFinite(Number(getPedidoItemsTotal(p))) ? (
                                      <div
                                        style={{
                                          marginTop: 10,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '6px 12px',
                                          borderRadius: 6,
                                          background: 'linear-gradient(180deg, rgba(239,246,255,.95) 0%, rgba(219,234,254,.95) 100%)',
                                          border: '1px solid rgba(59,130,246,.18)',
                                          color: '#1e3a8a',
                                          fontWeight: 800,
                                          fontSize: 12,
                                          width: '87%',
                                        }}
                                      >
                                        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', opacity: .8 }}>Total</span>
                                        <strong style={{ fontSize: 10 }}>{formatPedidoMoney(getPedidoItemsTotal(p))}</strong>
                                      </div>
                                    ) : null}
                                    <div className="crm-disp-card-foot">
                                      <div className="crm-farm-kanban-card__addr">
                                        {p.direccion === 'Retiro en mostrador'
                                          ? p.direccion
                                          : `Domicilio: ${p.direccion || 'Sin dirección'}`}
                                      </div>
                                    </div>
                                    {(sucursalNombre || isDomicilioPedido) ? (
                                      <div className={`crm-farm-branch-chip${sucursalNombre ? '' : ' is-pending'}`}>
                                        {/* <span>{isDomicilioPedido ? 'Envío' : 'Farmacia'}</span> */}
                                        <strong>{sucursalNombre || 'Envío a domicilio'} 🏥</strong>
                                      </div>
                                    ) : null}
                                  </>
                                )}
                                {botConversationId && !isCronicosPedido ? (
                                  <Link
                                    to={`/bot-farmacia?conv=${encodeURIComponent(botConversationId)}`}
                                    className="crm-farm-bot-open-link"
                                    draggable={false}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Abrir esta conversación en Bot Farmacia"
                                  >
                                    Ir a Bot Farmacia 🚑
                                  </Link>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="crm-farm-kanban-col__footnote">
                        {totalCards > 0 ? `${totalCards} total en ${col.title.toLowerCase()}` : `Sin pedidos en ${col.title.toLowerCase()}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : pageItems.length === 0 ? (
            <div className="crm-gd-empty">No hay pedidos de dispensa visibles (entregados/cancelados están en Historial)</div>
          ) : (
            <div className="crm-farm-mobile-list">
              {pageItems.map(p => {
                const fechaBadge = formatFechaBadge(p.fecha);
                const isPendienteAnterior = isPedidoPendienteAnterior(p);
                const isPendienteDerivacionMobile = String(p.estado) === 'pendiente_call_center';
                const pendienteMedMobile = getPendienteMedicamentoDisplay(p);
                const origenBadgeMobile = getFarmaciaOrigenBadge(p);
                const botConversationIdMobile = getPedidoConversationId(p);
                const isDomicilioMobile = isBotFarmaciaDomicilioPedido(p);
                const sucursalNombreMobile = getPedidoSucursalNombre(p);
                return (
                  <div
                    key={p.id}
                    className={`crm-icard${isPendienteDerivacionMobile ? ' crm-disp-mobile-pendiente' : ''}`}
                    style={{
                      marginBottom: 12,
                      border: isPendienteAnterior
                        ? '1px solid #f59e0b'
                        : (isPendienteDerivacionMobile ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid #DFE3EB'),
                      boxShadow: isPendienteAnterior
                        ? '0 0 0 2px rgba(245,158,11,.16), 0 1px 2px rgba(0,0,0,.05)'
                        : (isPendienteDerivacionMobile
                          ? '0 0 0 2px rgba(239,68,68,.12), 0 8px 22px rgba(185,28,28,.08)'
                          : '0 1px 2px rgba(0,0,0,.05)')
                    }}
                  >
                    <div className="crm-icard-h" style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', cursor: 'default', padding: '10px 12px', borderRadius: 12, background: '#fff', border: '1px solid rgba(148,163,184,.14)', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {fechaBadge ? (
                          <div
                            style={{
                              width: 46,
                              height: 46,
                              borderRadius: '50%',
                              background: 'linear-gradient(145deg, #eff6ff, #dbeafe)',
                              color: '#1d4ed8',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              boxShadow: '0 4px 12px rgba(37,99,235,.18)',
                              border: '1px solid rgba(59,130,246,.28)',
                              flexShrink: 0
                            }}
                          >
                            <div style={{ fontSize: 9, fontWeight: 800, lineHeight: 1 }}>{fechaBadge.dayMonth}</div>
                            <div style={{ fontSize: 9, fontWeight: 700, lineHeight: 1, marginTop: 2 }}>{fechaBadge.time}</div>
                          </div>
                        ) : null}
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '.01em', color: 'var(--hs-text)' }}>{getPedidoPacienteDisplay(p)}</div>
                          <div style={{ fontSize: 11, color: 'var(--hs-text-xlight)', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 4 }}>
                            {origenBadgeMobile ? (
                              <span className={`crm-farm-kanban-card__origen crm-farm-kanban-card__origen--${origenBadgeMobile.variant}`}>
                                {origenBadgeMobile.label}
                              </span>
                            ) : (
                              <span>Pedido de dispensa{getPedidoDni(p) ? ` · DNI ${formatDni(getPedidoDni(p))}` : ''}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: 7, width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {isPendienteAnterior ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px 8px', borderRadius: 999, fontWeight: 800, fontSize: 10, lineHeight: 1.1, background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d', textAlign: 'center', whiteSpace: 'nowrap' }}>Pendiente anterior</span>
                          ) : null}
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px 8px', minWidth: 120, borderRadius: 999, fontWeight: 700, fontSize: 10, lineHeight: 1.1, background: estadoTone(p.estado).bg, color: estadoTone(p.estado).text, border: `1px solid ${estadoTone(p.estado).border}`, textAlign: 'center', whiteSpace: 'nowrap' }}>{estadoLabel(p.estado)}</span>
                          {(sucursalNombreMobile || isDomicilioMobile) ? (
                            <span className={`crm-farm-branch-chip crm-farm-branch-chip--mobile${sucursalNombreMobile ? '' : ' is-pending'}`}>
                              <strong>{sucursalNombreMobile || 'Sucursal pendiente'}</strong>
                            </span>
                          ) : null}
                          {isPendienteDerivacionMobile ? (
                            <button
                              type="button"
                              className="crm-disp-mobile-pendiente-btn"
                              onClick={() => abrirEditarPendiente(p)}
                              title="Editar datos"
                            >
                              ✏️
                            </button>
                          ) : null}
                          {botConversationIdMobile ? (
                            <Link
                              to={`/bot-farmacia?conv=${encodeURIComponent(botConversationIdMobile)}`}
                              className="crm-farm-bot-open-chip"
                              title="Abrir en Bot Farmacia"
                            >
                              🤖 Bot
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => abrirDetallePedido(p)}
                            title="Ver detalle del pedido"
                            aria-label="Ver detalle del pedido"
                            style={{
                              border: '1px solid rgba(59,130,246,.22)',
                              background: 'linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)',
                              color: '#1d4ed8',
                              borderRadius: 999,
                              padding: '6px 10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: 800,
                              fontSize: 11,
                              boxShadow: '0 8px 20px rgba(37,99,235,.10)',
                              cursor: 'pointer'
                            }}
                          >
                            <span aria-hidden>👁</span>
                            <span>Detalle</span>
                          </button>
                          <button
                            aria-label={openId === p.id ? 'Cerrar' : 'Abrir'}
                            onClick={() => setOpenId(openId === p.id ? null : p.id)}
                            style={{ border: '1px solid rgba(148,163,184,.25)', background: '#fff', cursor: 'pointer', padding: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, transition: 'transform .28s cubic-bezier(.2,.9,.2,1)', boxShadow: '0 2px 8px rgba(15,23,42,.08)' }}
                          >
                            {openId === p.id ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', color: 'var(--hs-text)', opacity: 0.95 }} aria-hidden="true">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', color: 'var(--hs-text)', opacity: 0.95 }} aria-hidden="true">
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      ref={el => { contentRefs.current[p.id] = el; }}
                      style={{ overflow: 'hidden', transition: 'max-height .38s cubic-bezier(.2,.9,.2,1), opacity .28s ease', maxHeight: openId === p.id ? Math.max(contentRefs.current[p.id]?.scrollHeight || 0, 10000) + 'px' : '0px', opacity: openId === p.id ? 1 : 0 }}
                    >
                      <div className="crm-icard-b" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(148,163,184,.18)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isSmallLandscape ? '1fr 1fr' : 'minmax(220px,1fr) minmax(360px,1.7fr) minmax(230px,1fr)'), gap: 10, alignItems: 'stretch' }}>
                          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.18)', borderRadius: 12, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Medicamento</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, alignItems: 'flex-start' }}>
                              <div style={{ color: 'var(--hs-text)', fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                                {isPendienteDerivacionMobile
                                  ? pendienteMedMobile
                                  : formatMedicamentoLabel(p.medicamento || p.nombre_generico || p.nombre_generico_med || '—')}
                              </div>
                              {(p.presentacion || p.concentracion) ? (
                                <div style={{ marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <span style={{ background: '#eef2ff', color: '#1e40af', padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>
                                    {String(p.presentacion || p.concentracion)}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.18)', borderRadius: 12, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Dirección de entrega</div>
                            <div style={{ color: 'var(--hs-text)', fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>{p.direccion}</div>
                            {/* Mapa interactivo (Leaflet) */}
                            <button
                              type="button"
                              onClick={() => abrirMapaGrande({
                                address: p.direccion,
                                latitud: p.latitud,
                                longitud: p.longitud,
                                paciente: p.paciente,
                                medicamento: (() => {
                                  const base = p.nombre_generico || p.medicamento || '';
                                  const concentration = p.presentacion || p.concentracion || '';
                                  if (!base) return concentration || '';
                                  return concentration ? `${base} · ${concentration}` : base;
                                })(),
                                laboratorio: (p.laboratorio || p.lab || ''),
                                nroPedido: (p.nro_pedido || '—')
                              })}
                              style={{ marginTop: 8, width: '100%', borderRadius: 10, overflow: 'hidden', border: 'none', padding: 0, background: '#fff', cursor: 'pointer', textAlign: 'left' }}
                              title="Ver mapa ampliado"
                            >
                              {/* lazy-load component to avoid breaking builds when deps not installed */}
                              {openId === p.id && typeof window !== 'undefined' ? (
                                <React.Suspense fallback={<div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hs-text-xlight)' }}>Cargando mapa…</div>}>
                                  <DeliveryMap address={p.direccion} latitude={p.latitud} longitude={p.longitud} height={125} interactive={false} />
                                </React.Suspense>
                              ) : null}
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateRows: 'auto auto', gap: 8 }}>
                            <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.18)', borderRadius: 12, padding: '10px 12px' }}>
                              <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>N° pedido farmacia</div>
                              <div id={`nro-farmacia-${p.id}`} style={{ color: 'var(--hs-text)', fontFamily: 'monospace', fontWeight: 700, fontSize: 13, wordBreak: 'break-word' }}>{p.nro_pedido || '—'}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                              {p.estado === 'pendiente_call_center' ? (
                                <button className="crm-sb-btn" style={{ margin: 0, width: 'auto', minHeight: 34, padding: '6px 12px', borderRadius: 999, fontWeight: 700, border: '1px solid #d2e2f4', color: '#1c4f86', background: '#fff', fontSize: 12 }} onClick={() => abrirFarmacia(p.id)}>Generar pedido en farmacia</button>
                              ) : (
                                <button className="crm-sb-btn" style={{ margin: 0, width: 'auto', minHeight: 34, padding: '6px 12px', borderRadius: 999, fontWeight: 700, borderColor: '#d2e2f4', color: '#1d4ed8', background: '#fff', fontSize: 12 }} onClick={() => abrirEstado(p.id)}>Actualizar estado</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {isMobile ? (
          <div className="crm-farm-mobile-pagination">
            <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-end' }}>
              <button
                className="datatable-pagination-button"
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                ⏮
              </button>

              <button
                className="datatable-pagination-button"
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                ◀
              </button>

              <span style={{ marginTop: 4 }}>{`Pag ${page} de ${totalPages}`}</span>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: isMobile ? 0 : 12 }}>
                <input
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goToInputPage(); } }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={`1–${totalPages}`}
                  style={{ width: 22, height: 22, padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, textAlign: 'center' }}
                />
                <button
                  type="button"
                  style={{ padding: '6px 10px', borderRadius: 999, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  onClick={goToInputPage}
                >
                  Ir
                </button>
              </div>

              <button
                className="datatable-pagination-button"
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                ▶
              </button>

              <button
                className="datatable-pagination-button"
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              >
                ⏭
              </button>
            </div>
          </div>
        ) : null}

      </div>

      <div
        role="presentation"
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right${pendienteEditDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setPendienteEditDrawerOpen(false);
        }}
      >
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Editar pedido sin gestión"
          className={`crm-gd-drawer-panel crm-disp-pendiente-drawer${pendienteEditDrawerOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'}`}
          style={drawerPanelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-gd-drawer-head">
            <div>
              <div className="crm-gd-drawer-head__title">Editar derivación pendiente</div>
              <p className="crm-gd-drawer-head__sub">
                {pendienteEditPedido ? getPedidoPacienteDisplay(pendienteEditPedido) : 'Pedido sin gestión'}
              </p>
            </div>
            <button type="button" className="crm-gd-btn" onClick={() => setPendienteEditDrawerOpen(false)}>Cerrar</button>
          </div>
          <div className="crm-gd-drawer-body">
            <div className="crm-disp-pendiente-drawer-hero">
              <div className="crm-disp-pendiente-drawer-hero__icon" aria-hidden>📋</div>
              <div>
                <strong>Datos operativos del pedido</strong>
                <p>Corregí dirección, DNI o notas antes de derivar a farmacia.</p>
              </div>
            </div>
            {pendienteEditPedido ? (
              <div className="crm-disp-pendiente-drawer-med">
                <span className="crm-disp-pendiente-drawer-med__label">Medicamento</span>
                <strong>{formatMedicamentoLabel(pendienteEditPedido.medicamento || '')}</strong>
                {(pendienteEditPedido.presentacion || pendienteEditPedido.concentracion) ? (
                  <span>{String(pendienteEditPedido.presentacion || pendienteEditPedido.concentracion)}</span>
                ) : null}
              </div>
            ) : null}
            <div className="crm-disp-pendiente-drawer-form">
              <label className="crm-gd-field-label" htmlFor="disp-pend-dni">DNI paciente</label>
              <input
                id="disp-pend-dni"
                className="crm-gd-input"
                value={pendienteEditDni}
                onChange={(e) => setPendienteEditDni(e.target.value)}
                placeholder="Ej. 30123456"
              />
              <label className="crm-gd-field-label" htmlFor="disp-pend-dir">Dirección de entrega</label>
              <textarea
                id="disp-pend-dir"
                className="crm-gd-textarea"
                rows={3}
                value={pendienteEditDireccion}
                onChange={(e) => setPendienteEditDireccion(e.target.value)}
                placeholder="Calle, número, barrio, referencia…"
              />
              <label className="crm-gd-field-label" htmlFor="disp-pend-notas">Notas internas</label>
              <textarea
                id="disp-pend-notas"
                className="crm-gd-textarea"
                rows={4}
                value={pendienteEditNotas}
                onChange={(e) => setPendienteEditNotas(e.target.value)}
                placeholder="Contexto de derivación, observaciones del call center…"
              />
              {pendienteEditPedido?.telefono ? (
                <p className="crm-disp-pendiente-drawer-phone">
                  Teléfono en padrón: <strong>{pendienteEditPedido.telefono}</strong>
                </p>
              ) : null}
            </div>
            <div className="crm-disp-pendiente-drawer-actions">
              {getPedidoConversationId(pendienteEditPedido) ? (
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--ghost"
                  onClick={() => irAConversacionPedido(pendienteEditPedido)}
                >
                  💬 Ir a conversación
                </button>
              ) : null}
              <button
                type="button"
                className="crm-gd-btn crm-gd-btn--primary"
                onClick={guardarEdicionPendiente}
                disabled={pendienteEditSaving}
              >
                {pendienteEditSaving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <div
        role="presentation"
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right${pendienteInfoDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setPendienteInfoDrawerOpen(false);
        }}
      >
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Detalle de pendiente anterior"
          className={`crm-gd-drawer-panel${pendienteInfoDrawerOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'}`}
          style={drawerPanelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-gd-drawer-head">
            <div>
              <div className="crm-gd-drawer-head__title">Pendiente anterior</div>
              <p className="crm-gd-drawer-head__sub">Detalle del pedido para seguimiento operativo.</p>
            </div>
            <button type="button" className="crm-gd-btn" onClick={() => setPendienteInfoDrawerOpen(false)}>Cerrar</button>
          </div>
          <div className="crm-gd-drawer-body">
            <div className="crm-farm-alert-banner">
              <div className="crm-farm-alert-banner__chip">Seguimiento prioritario</div>
              <p className="crm-farm-alert-banner__text">
                Este pedido quedó pendiente del día <strong>{pendienteDrawerFecha}</strong> y requiere seguimiento prioritario.
              </p>
            </div>
            <div className="crm-gd-med-box" style={{ marginTop: 12 }}>
              <dl className="crm-farm-detail-grid">
                <dt>Paciente</dt><dd>{pendienteDrawerPaciente}</dd>
                <dt>DNI</dt><dd>{pendienteDrawerDni}</dd>
                <dt>Estado</dt><dd>{pendienteDrawerEstado}</dd>
                <dt>Fecha pedido</dt><dd>{pendienteDrawerFecha}</dd>
                <dt>Medicamento</dt><dd>{pendienteDrawerMedicamento}</dd>
                <dt>Presentación</dt><dd>{pendienteDrawerPresentacion}</dd>
                <dt>Dirección</dt><dd>{pendienteDrawerDireccion}</dd>
                <dt>N° pedido</dt><dd>{pendienteDrawerNumeroPedido}</dd>
                <dt>Notas</dt><dd>{pendienteDrawerNotas}</dd>
              </dl>
            </div>
          </div>
        </aside>
      </div>

      {historialGlobalOpen ? (
        <div
          role="presentation"
          className="crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right crm-gd-drawer-backdrop--open"
          onClick={(e) => {
            if (e.target === e.currentTarget) setHistorialGlobalOpen(false);
          }}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Historial global de pedidos"
            className="crm-gd-drawer-panel crm-gd-drawer-panel--right-open"
            style={historialPanelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crm-gd-drawer-head">
              <div>
                <div className="crm-gd-drawer-head__title">Historial global</div>
                <p className="crm-gd-drawer-head__sub">Pedidos entregados o cancelados, fuera del tablero activo.</p>
              </div>
              <button type="button" className="crm-gd-btn" onClick={() => setHistorialGlobalOpen(false)}>Cerrar</button>
            </div>
            <div className="crm-gd-drawer-body">
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: 10, alignItems: 'end', marginBottom: 10 }}>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-pac-search">Paciente (DNI)</label>
                  <input id="disp-hist-pac-search" className="crm-gd-input" value={historialGlobalPacienteSearch} onChange={(e) => setHistorialGlobalPacienteSearch(e.target.value)} placeholder="Buscar paciente…" />
                </div>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-pac-select">Selección</label>
                  <select id="disp-hist-pac-select" className="crm-gd-select" value={historialGlobalDni} onChange={(e) => setHistorialGlobalDni(e.target.value)}>
                    <option value="">Seleccioná un paciente</option>
                    {filteredHistorialPacientes.map((p) => (
                      <option key={`hgp-disp-${p.dni}`} value={p.dni}>{`${p.nombre} · DNI ${String(p.dni).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="crm-gd-filter-chip" style={{ marginBottom: 10 }}>{filteredHistorialGlobalItems.length} registros</p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr repeat(4, 1fr)', gap: 8, marginBottom: 12, alignItems: 'end' }}>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-search">Buscar</label>
                  <input id="disp-hist-search" className="crm-gd-input" value={historialGlobalSearch} onChange={(e) => setHistorialGlobalSearch(e.target.value)} placeholder="Medicamento, nro o dirección…" disabled={!historialGlobalDni} />
                </div>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-desde">Desde</label>
                  <input id="disp-hist-desde" type="date" className="crm-gd-input" value={historialGlobalDesde} onChange={(e) => setHistorialGlobalDesde(e.target.value)} disabled={!historialGlobalDni} />
                </div>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-hasta">Hasta</label>
                  <input id="disp-hist-hasta" type="date" className="crm-gd-input" value={historialGlobalHasta} onChange={(e) => setHistorialGlobalHasta(e.target.value)} disabled={!historialGlobalDni} />
                </div>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-order">Orden</label>
                  <select id="disp-hist-order" className="crm-gd-select" value={historialGlobalOrder} onChange={(e) => setHistorialGlobalOrder(e.target.value)} disabled={!historialGlobalDni}>
                    <option value="fecha_desc">Más reciente</option>
                    <option value="fecha_asc">Más antiguo</option>
                  </select>
                </div>
                <div>
                  <label className="crm-gd-field-label" htmlFor="disp-hist-size">Mostrar</label>
                  <select id="disp-hist-size" className="crm-gd-select" value={historialGlobalItemsPerPage} onChange={(e) => setHistorialGlobalItemsPerPage(Number(e.target.value))} disabled={!historialGlobalDni}>
                    <option value={6}>6</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
              {!historialGlobalDni ? (
                <div className="crm-gd-empty">
                  <div style={{ fontSize: 26, marginBottom: 6 }}>🗂️</div>
                  <strong>Sin historial para mostrar</strong>
                  <p style={{ margin: '6px 0 0', fontSize: 13 }}>Seleccioná un paciente para ver sus pedidos entregados.</p>
                </div>
              ) : pageHistorialGlobalItems.length === 0 ? (
                <div className="crm-gd-empty">
                  <div style={{ fontSize: 26, marginBottom: 6 }}>🗂️</div>
                  <strong>Sin historial para mostrar</strong>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {pageHistorialGlobalItems.map((p) => (
                    <article key={`hgd-${p.id}`} className="crm-farm-hist-item">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <div className="crm-farm-hist-item__med">{formatMedicamentoLabel(p.medicamento || '')}</div>
                          <span className="crm-farm-status" style={estadoStatusStyle(p.estado)}>{estadoLabel(p.estado)}</span>
                        </div>
                        {(p.presentacion || p.concentracion) ? <span className="crm-gd-pill" style={{ marginTop: 8 }}>{String(p.presentacion || p.concentracion)}</span> : null}
                        <div style={{ marginTop: 8, fontSize: 12, color: '#33475b', fontWeight: 800 }}>{getPedidoPacienteDisplay(p)}{getPedidoDni(p) ? ` · DNI ${formatDni(getPedidoDni(p))}` : ''}</div>
                        {p.direccion ? <div style={{ marginTop: 6, fontSize: 12, color: '#516f90' }}>{p.direccion}</div> : null}
                        {p.fecha ? <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', fontWeight: 700 }}>Entregado: {formatFechaDisplay(p.fecha)}</div> : null}
                      </div>
                    </article>
                  ))}
                  <div className="crm-farm-hist-pagination">
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                      Mostrando {Math.min((historialGlobalPage - 1) * historialGlobalItemsPerPage + 1, filteredHistorialGlobalItems.length)}-{Math.min(historialGlobalPage * historialGlobalItemsPerPage, filteredHistorialGlobalItems.length)} de {filteredHistorialGlobalItems.length}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button type="button" className="crm-gd-btn" disabled={historialGlobalPage <= 1} onClick={() => setHistorialGlobalPage((prev) => Math.max(1, prev - 1))}>◀</button>
                      <span style={{ fontSize: 12, color: '#475569', fontWeight: 800 }}>Página {historialGlobalPage} / {totalHistorialGlobalPages}</span>
                      <button type="button" className="crm-gd-btn" disabled={historialGlobalPage >= totalHistorialGlobalPages} onClick={() => setHistorialGlobalPage((prev) => Math.min(totalHistorialGlobalPages, prev + 1))}>▶</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      ) : null}

      {modalFarmaciaOpen && (
        <div className="crm-cw-forward-overlay">
          <div className="crm-farm-modal-box">
            <div className="crm-cw-forward-head"><strong>Gestionar pedido de farmacia</strong></div>
            <div className="crm-farm-modal-body">
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>Generá el pedido en el sistema de farmacia</p>
              <input type="hidden" value={selectedFarmId || ''} />
              <div style={{ marginBottom: 10 }}>
                <label className="crm-gd-field-label" htmlFor="disp-farm-nro">N° de pedido en farmacia</label>
                <input id="disp-farm-nro" className="crm-gd-input" value={farmNro} onChange={(e) => setFarmNro(e.target.value)} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label className="crm-gd-field-label" htmlFor="disp-farm-notas">Notas</label>
                <textarea id="disp-farm-notas" className="crm-gd-textarea" value={farmNotas} onChange={(e) => setFarmNotas(e.target.value)} />
              </div>
              <div className="crm-farm-modal-actions">
                <button type="button" className="crm-gd-btn" onClick={() => setModalFarmaciaOpen(false)}>Cancelar</button>
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--primary"
                  onClick={confirmarPedidoFarmacia}
                  disabled={!String(farmNro || '').trim() || !String(farmNotas || '').trim()}
                >
                  Generar pedido en farmacia
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalEstadoOpen && (
        <div className="crm-cw-forward-overlay">
          <div className="crm-farm-modal-box">
            <div className="crm-cw-forward-head"><strong>Actualizar estado</strong></div>
            <div className="crm-farm-modal-body">
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>Seleccioná el nuevo estado</p>
              <div style={{ marginBottom: 10 }}>
                <label className="crm-gd-field-label" htmlFor="disp-nuevo-estado">Nuevo estado</label>
                {(() => {
                  const currentPedido = (data.pedidos_farmacia || []).find(p => String(p.id) === String(selectedFarmId) || String(p.receta_id) === String(selectedFarmId));
                  const currentEstado = currentPedido ? currentPedido.estado : null;
                  const estadoOrder = ['pendiente_call_center', 'pedido_generado_farmacia', 'cancelado'];
                  const currentIndex = estadoOrder.indexOf(currentEstado);
                  const nextEstado = (currentIndex >= 0 && currentIndex < (estadoOrder.length - 1))
                    ? estadoOrder[currentIndex + 1]
                    : null;
                  const isEnabledOption = (opt) => {
                    if (opt === 'cancelado') return true;
                    if (!currentEstado) return opt === 'pedido_generado_farmacia';
                    if (currentEstado === 'cancelado') return false;
                    return opt === nextEstado;
                  };
                  const terminalEstado = currentEstado === 'cancelado';
                  return (
                    <select id="disp-nuevo-estado" className="crm-gd-select" value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)} disabled={terminalEstado}>
                      <option value="pedido_generado_farmacia" disabled={!isEnabledOption('pedido_generado_farmacia')}>Pedido generado en farmacia</option>
                      <option value="cancelado" disabled={!isEnabledOption('cancelado')}>Cancelado</option>
                    </select>
                  );
                })()}
              </div>
              <div className="crm-farm-modal-actions">
                <button type="button" className="crm-gd-btn" onClick={() => setModalEstadoOpen(false)}>Cancelar</button>
                {(() => {
                  const currentPedido = (data.pedidos_farmacia || []).find(p => String(p.id) === String(selectedFarmId) || String(p.receta_id) === String(selectedFarmId));
                  const currentEstado = currentPedido ? currentPedido.estado : null;
                  const terminalEstado = currentEstado === 'cancelado';
                  return (
                    <button type="button" className="crm-gd-btn crm-gd-btn--primary" onClick={confirmarEstado} disabled={terminalEstado} title={terminalEstado ? 'No se puede actualizar: pedido finalizado' : ''}>Actualizar</button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {trazabilidadPedido && (
        <div className="crm-cw-forward-overlay" onClick={() => setTrazabilidadPedido(null)}>
          <div
            className="crm-farm-modal-box crm-farm-traza-modal crm-disp-traza-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Trazabilidad del pedido"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crm-disp-traza-head">
              <div className="crm-disp-traza-head__main">
                <span className="crm-disp-traza-head__icon" aria-hidden="true">🕘</span>
                <div className="crm-disp-traza-head__copy">
                  <span className="crm-disp-traza-head__eyebrow">Trazabilidad del pedido</span>
                  <strong className="crm-disp-traza-head__patient">
                    {trazabilidadPedido.paciente || 'Paciente'}
                  </strong>
                  {trazabilidadPedido.nro_pedido ? (
                    <span className="crm-disp-traza-head__nro">N° {trazabilidadPedido.nro_pedido}</span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="crm-disp-traza-head__close"
                onClick={() => setTrazabilidadPedido(null)}
                aria-label="Cerrar trazabilidad"
              >
                ✕
              </button>
            </div>
            <div className="crm-farm-modal-body crm-disp-traza-body">
              {trazabilidadLoading ? (
                <div className="crm-disp-traza-loading">
                  <span className="crm-disp-traza-loading__spinner" aria-hidden="true" />
                  Cargando historial…
                </div>
              ) : trazabilidadItems.length === 0 ? (
                <div className="crm-disp-traza-empty">
                  <span className="crm-disp-traza-empty__icon" aria-hidden="true">📋</span>
                  Sin movimientos registrados todavía.
                </div>
              ) : (
                <ol className="crm-disp-traza-timeline">
                  {trazabilidadItems.map((item, idx) => {
                    const toEstado = item.new_estado || item.estado;
                    const note = item.notas_cambio || item.nota;
                    return (
                      <li key={item.id || `hist-${idx}`} className="crm-disp-traza-item">
                        <div className="crm-disp-traza-item__rail" aria-hidden="true">
                          <span className="crm-disp-traza-item__dot" />
                        </div>
                        <div className="crm-disp-traza-item__card">
                          <div className="crm-disp-traza-item__estados">
                            {item.previous_estado ? (
                              <span
                                className="crm-disp-traza-pill"
                                style={estadoStatusStyle(item.previous_estado)}
                              >
                                {estadoLabel(item.previous_estado)}
                              </span>
                            ) : (
                              <span className="crm-disp-traza-pill crm-disp-traza-pill--muted">Inicio</span>
                            )}
                            <span className="crm-disp-traza-item__arrow" aria-hidden="true">→</span>
                            <span
                              className="crm-disp-traza-pill crm-disp-traza-pill--to"
                              style={estadoStatusStyle(toEstado)}
                            >
                              {estadoLabel(toEstado)}
                            </span>
                          </div>
                          <div className="crm-disp-traza-item__meta">
                            <span className="crm-disp-traza-item__time">
                              {formatHistFecha(item.created_at || item.fecha)}
                            </span>
                            {item.changed_by ? (
                              <span className="crm-disp-traza-item__actor">{item.changed_by}</span>
                            ) : null}
                            {item.origen ? (
                              <span className="crm-disp-traza-item__origen">{origenHistLabel(item.origen)}</span>
                            ) : null}
                          </div>
                          {note ? (
                            <div className="crm-disp-traza-item__note">{note}</div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
              <div className="crm-disp-traza-actions">
                <button type="button" className="crm-gd-btn crm-gd-btn--primary" onClick={() => setTrazabilidadPedido(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {notasPopupPedido && (
        <div className="crm-cw-forward-overlay" onClick={() => setNotasPopupPedido(null)}>
          <div
            className="crm-farm-modal-box crm-farm-notas-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Nota del pedido"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crm-cw-forward-head">
              <strong>Nota del pedido</strong>
            </div>
            <div className="crm-farm-modal-body">
              <div className="crm-farm-notas-modal__meta">
                <span className="crm-farm-notas-modal__chip">📝 Observación</span>
                {notasPopupPedido.nro_pedido ? (
                  <span className="crm-farm-notas-modal__ref">N° {notasPopupPedido.nro_pedido}</span>
                ) : null}
              </div>
              <div className="crm-farm-notas-modal__patient">
                {notasPopupPedido.paciente || 'Paciente'}
              </div>
              <div className="crm-farm-notas-modal__med-box">
                <div className="crm-farm-notas-modal__med-name">
                  {formatMedicamentoLabel(notasPopupPedido.medicamento)}
                </div>
                {(notasPopupPedido.presentacion || String(notasPopupPedido.laboratorio || '').trim()) ? (
                  <div className="crm-farm-notas-modal__med-meta">
                    {notasPopupPedido.presentacion ? (
                      <span className="crm-farm-notas-modal__pres">{String(notasPopupPedido.presentacion)}</span>
                    ) : null}
                    {String(notasPopupPedido.laboratorio || '').trim() ? (
                      <span className="crm-farm-notas-modal__lab">
                        <span className="crm-farm-notas-modal__lab-ico" aria-hidden>🏭</span>
                        {formatMedicamentoLabel(notasPopupPedido.laboratorio)}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="crm-farm-notas-modal__quote">
                <span className="crm-farm-notas-modal__quote-mark crm-farm-notas-modal__quote-mark--open" aria-hidden>“</span>
                <p>{getPedidoNotasRaw(notasPopupPedido)}</p>
                <span className="crm-farm-notas-modal__quote-mark crm-farm-notas-modal__quote-mark--close" aria-hidden>”</span>
              </div>
              <div className="crm-farm-modal-actions">
                <button type="button" className="crm-gd-btn crm-gd-btn--primary" onClick={() => setNotasPopupPedido(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pedidoDetailOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setPedidoDetailOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.3)', zIndex: 1210, display: 'flex', justifyContent: 'flex-end' }}
        >
          <aside
            style={{
              width: isMobile ? '100vw' : 'min(460px, 92vw)',
              height: '100dvh',
              background: '#ffffff',
              borderLeft: isMobile ? 'none' : '1px solid #e2e8f0',
              boxShadow: '-10px 0 30px rgba(15,23,42,.08)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* HEADER */}
            <div
              style={{
                padding: '16px',
                borderBottom: '1px solid #f1f5f9',
                background: '#ffffff',
                flexShrink: 0
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#2563eb' }}>
                    Detalle de pedido
                  </div>
                  <div style={{ marginTop: 4, fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                    {pedidoDetailData?.paciente || 'Cliente mostrador'}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontWeight: 600, fontSize: 11 }}>
                      #{pedidoDetailData?.nro_pedido || formatAtencionPedidoNro(pedidoDetailData?.atencion_pedido_id)}
                    </span>
                    <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontWeight: 600, fontSize: 11 }}>
                      {pedidoDetailData?.metodo_entrega === 'domicilio' ? 'Envío a domicilio' : 'Retiro en sucursal'}
                    </span>
                    {pedidoDetailData?.sucursal_nombre && (
                      <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontWeight: 600, fontSize: 11 }}>
                        {pedidoDetailData.sucursal_nombre}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPedidoDetailOpen(false)}
                  aria-label="Cerrar detalle del pedido"
                  style={{
                    border: 'none',
                    background: '#f1f5f9',
                    color: '#64748b',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontSize: 12,
                    transition: 'background 0.2s'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* CONTENIDO PRINCIPAL CON SCROLL */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                background: '#f8fafc'
              }}
            >
              {pedidoDetailError && (
                <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', fontWeight: 600, fontSize: 12 }}>
                  {pedidoDetailError}
                </div>
              )}

              {/* CONTENEDOR DE METADATA (RESUMEN + DIRECCIÓN) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Fila superior: Dos columnas limpias */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.02em' }}>Artículos</div>
                    <div style={{ marginTop: 2, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{pedidoDetailItems.length} prod.</div>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.02em' }}>Modalidad</div>
                    <div style={{ marginTop: 2, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      {pedidoDetailData?.metodo_entrega === 'domicilio' ? 'Envío' : 'Retiro'}
                    </div>
                  </div>
                </div>

                {/* Fila inferior: Dirección a ancho completo que salta para abajo perfectamente */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.02em' }}>Destino / Entrega</div>
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 600, color: '#334155', lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {pedidoDetailData?.direccion === 'Retiro en mostrador'
                      ? (pedidoDetailData?.sucursal_nombre || 'Retiro por mostrador')
                      : (pedidoDetailData?.direccion || 'Sin dirección especificada')}
                  </div>
                </div>
              </div>

              {/* LISTA DE PRODUCTOS */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Items solicitados</span>
                  {pedidoDetailLoading && <span style={{ fontSize: 11, color: '#64748b' }}>Actualizando…</span>}
                </div>

                {pedidoDetailItems.length ? (
                  pedidoDetailItems.map((item, index) => {
                    const qty = getPedidoItemCantidad(item);
                    const unit = getPedidoOptionalNumber(item?.precio_unitario);
                    const lineTotal = getPedidoItemTotal(item);

                    return (
                      <article
                        key={`${pedidoDetailData?.id || 'pedido'}-item-${index}`}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 12,
                          padding: '12px',
                          background: '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8
                        }}
                      >
                        {/* Nombre e indicador de cantidad */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', lineHeight: 1.2 }}>
                              {item?.producto || 'Producto'}
                            </div>
                            {item?.presentacion && (
                              <div style={{ marginTop: 2, fontSize: 11, color: '#64748b' }}>
                                {String(item.presentacion)}
                              </div>
                            )}
                          </div>
                          <span style={{ flexShrink: 0, padding: '2px 6px', borderRadius: 4, background: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: 11 }}>
                            x{qty}
                          </span>
                        </div>

                        {/* Fila compacta de metadata técnica y costos */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed #f1f5f9', paddingTop: 8, fontSize: 11, color: '#64748b' }}>
                          <div>
                            ID: <span style={{ fontFamily: 'monospace', color: '#334155', fontWeight: 600 }}>{item?.idproducto || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <span>{formatPedidoMoney(unit)} c/u</span>
                            <span style={{ fontWeight: 700, color: '#1e3a8a' }}>{formatPedidoMoney(lineTotal)}</span>
                          </div>
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12, color: '#64748b', fontSize: 13 }}>
                    No hay ítems cargados en este pedido.
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER FIJO CON TOTAL */}
            <div
              style={{
                padding: '16px',
                borderTop: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Total estimado</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                {formatPedidoMoney(pedidoDetailTotal)}
              </span>
            </div>
          </aside>
        </div>
      )}

      {mapDrawerOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setMapDrawerOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.36)', zIndex: 1200, display: 'flex', justifyContent: 'flex-end' }}
        >
          <aside
            style={{
              width: isMobile ? '100vw' : 'min(700px, 92vw)',
              height: '100dvh',
              background: 'linear-gradient(180deg,#ffffff,#f8fafc)',
              borderLeft: isMobile ? 'none' : '1px solid rgba(148,163,184,.24)',
              boxShadow: '-24px 0 50px rgba(15,23,42,.25)',
              transform: mapDrawerOpen ? 'translateX(0)' : 'translateX(102%)',
              transition: 'transform .36s cubic-bezier(.22,.9,.22,1)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1201
            }}
          >
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(148,163,184,.2)', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', color: '#0f172a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>Mapa de entrega</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{mapDrawerPaciente || 'Paciente'}</div>
                </div>
                <button onClick={() => setMapDrawerOpen(false)} style={{ border: '1px solid rgba(148,163,184,.35)', background: '#fff', color: '#0f172a', borderRadius: 10, padding: '7px 11px', cursor: 'pointer', fontWeight: 700 }}>Cerrar</button>
              </div>
            </div>

            <div style={{ padding: isMobile ? 12 : 16, display: 'flex', flexDirection: 'column', gap: 10, height: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.22)', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Medicamento</div>
                  <div style={{ color: '#0f172a', fontWeight: 800, fontSize: 14 }}>{formatMedicamentoLabel(mapDrawerMedicamento)}</div>
                  <div style={{ color: '#475569', fontSize: 12, marginTop: 4 }}>{mapDrawerLaboratorio || '—'}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.22)', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>N° pedido farmacia</div>
                  <div style={{ color: '#0f172a', fontWeight: 800, fontSize: 14, fontFamily: 'monospace' }}>{mapDrawerNroPedido || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em' }}>Dirección</div>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 14, background: '#fff', border: '1px solid rgba(148,163,184,.25)', borderRadius: 12, padding: '9px 11px' }}>
                {mapDrawerAddress || 'Sin dirección informada'}
              </div>
              <div style={{ marginTop: 4, borderRadius: 14, overflow: 'hidden', border: 'none', background: '#fff', flex: 1, minHeight: isMobile ? 300 : 420 }}>
                {typeof window !== 'undefined' ? (
                  <React.Suspense fallback={<div style={{ height: '100%', width: '100%', minHeight: isMobile ? 300 : 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hs-text-xlight)' }}>Cargando mapa ampliado…</div>}>
                    <DeliveryMap address={mapDrawerAddress} latitude={mapDrawerLatitud} longitude={mapDrawerLongitud} height={Math.max((window.innerHeight || 820) - (isMobile ? 310 : 240), isMobile ? 300 : 420)} interactive />
                  </React.Suspense>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
